import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navbar
      "nav.partnerWithUs": "Partner with us",
      "nav.platform": "Platform",
      "nav.services": "Services",
      "nav.whyConncct": "Why Conncct",
      "nav.resources": "Resources",
      "nav.about": "About",
      "nav.careers": "Careers",
      "nav.pricing": "Pricing",
      "nav.alreadyHaveAccount": "Already have an account?",
      "nav.signIn": "Login",
      "nav.arabic": "العربية",
      "nav.getDemo": "Get a demo",
      "nav.createAccount": "Create a Free Account",

      // Top Banner
      "topBanner.winUpTo": "Get your free startup readiness score",
      "topBanner.winDesc": "see exactly where you stand.",
      "topBanner.applyNow": "Start free →",

      // Admin
      "admin.searchPlaceholder": "Search by Account Number, Email, Phone or Name",
      "admin.clientsOverview": "Clients Overview",
      "admin.newClients": "New Clients",
      "admin.last30Days": "Last 30 Days",
      "admin.allClients": "All Clients",
      "admin.totalRegistered": "Total Registered",
      "admin.applyFilters": "Apply Filters",
      "admin.quickFilters": "Quick Filters",
      "admin.risk.critical": "Critical Risk",
      "admin.risk.veryHigh": "Very High Risk",
      "admin.risk.high": "High Risk",
      "admin.risk.medium": "Medium Risk",
      "admin.risk.semiReady": "Semi-Ready",
      "admin.risk.investorReady": "Investor-Ready",
      "admin.risk.exceptional": "Exceptional",
      "admin.product.raising": "Raising",
      "admin.product.consulting": "Consulting",
      "admin.score.yes": "Yes",
      "admin.score.no": "No",
      "admin.referralTable": "Referral Table",
      "admin.table.name": "Name",
      "admin.table.email": "Email",
      "admin.table.irScore": "IR Score",
      "admin.table.phone": "Phone",
      "admin.table.products": "Products",
      "admin.table.joined": "Joined",
      "admin.table.action": "Action",
      "admin.table.view": "View",
      "admin.filter.allCountries": "All Countries",
      "admin.filter.startDate": "Start Date",
      "admin.filter.endDate": "End Date",
      "admin.filter.allProducts": "All Products",
      "admin.filter.allScores": "All Scores",
      "admin.filter.allResults": "All Results",
      "admin.filter.newClients": "New Clients",
      "admin.filter.paidClients": "Paid",
      "admin.filter.nonPaidClients": "Non-Paid",
      "admin.filter.completedKYC": "Completed KYC",
      "admin.filter.notCompletedKYC": "Not Completed KYC",

      // Admin User Detail
      "admin.userDetail.loading": "Loading user details...",
      "admin.userDetail.notFound": "User not found",
      "admin.userDetail.back": "Back to Admin",
      "admin.userDetail.tabs.goal": "Goal",
      "admin.userDetail.tabs.irScore": "IR Score",
      "admin.userDetail.tabs.profile": "Profile",
      "admin.userDetail.tabs.companyProfile": "Company Profile",
      "admin.userDetail.tabs.financials": "Financials",
      "admin.userDetail.tabs.kpis": "KPIs",
      "admin.userDetail.tabs.dataRoom": "Data room",
      "admin.userDetail.tabs.raiseCapital": "Raise Capital",
      "admin.userDetail.raiseCapitalSubmission": "Raise Capital submission",
      "admin.userDetail.viewAnswers": "View answers",
      "admin.userDetail.noGoalAssessment": "No goal assessment submitted yet.",
      "admin.userDetail.noRaiseCapital": "No raise capital submission found for this user.",
      "admin.userDetail.submitted": "Submitted",

      // Assessment Questions & Options
      "assessment.goal.focus": "What's your #1 focus right now?",
      "assessment.goal.focus.raise": "Raise Funds",
      "assessment.goal.focus.product": "Build Product",
      "assessment.goal.focus.sales": "Grow Sales",
      "assessment.goal.focus.ops": "Setup Ops",
      "assessment.goal.time": "Where does most of your time go?",
      "assessment.goal.time.investor": "Investor Work",
      "assessment.goal.time.product": "Product Build",
      "assessment.goal.time.sales": "Sales Marketing",
      "assessment.goal.time.ops": "Ops Legal",
      "assessment.goal.win": "What would a 6-month win look like for you?",
      "assessment.goal.win.funded": "Funded Round",
      "assessment.goal.win.live": "Live Product",
      "assessment.goal.win.revenue": "Revenue Growth",
      "assessment.goal.win.ops": "Clean Ops",
      "assessment.goal.holding": "What's holding you back most today?",
      "assessment.goal.holding.knowhow": "Investor Know-How",
      "assessment.goal.holding.test": "Build Test",
      "assessment.goal.holding.customer": "Customer Growth",
      "assessment.goal.holding.legal": "Legal Setup",
      "assessment.goal.step": "What's one step you can take this week?",
      "assessment.goal.step.now": "Fundraise Now",
      "assessment.goal.step.features": "Build Features",
      "assessment.goal.step.marketing": "Launch Marketing",
      "assessment.goal.step.coo": "Hire COO",

      "assessment.raise.amount": "How much are you looking to raise?",
      "assessment.raise.round": "What is the Round that you are raising for?",
      "assessment.raise.capitalType": "What type of capital are you raising?",
      "assessment.raise.valuation": "Do you have a target valuation or cap? (optional)",
      "assessment.raise.companyInfo": "Tell us about your company",
      "assessment.raise.website": "Website",

      // IR Score Data Room
      "assessment.ir.financialHeading": "Financial & Operational Data",
      "assessment.ir.strategicHeading": "Strategic & Team Assessment",
      "assessment.ir.runway": "What is your current runway (initial capital vs. monthly burn rate)?",
      "assessment.ir.capital": "How much capital are you planning to raise in your current or upcoming funding round?",
      "assessment.ir.burnRate": "What is your annual/monthly operating cost (Burn Rate)?",
      "assessment.ir.valuation": "What is your most recent company valuation (post-money or pre-money)?",
      "assessment.ir.timeline": "What is your expected revenue timeline to reach breakeven?",
      "assessment.ir.milestone": "What is your expected timeline to achieve the next key milestone that supports reaching cash-flow breakeven?",
      "assessment.ir.debt": "Do you have any debt?",
      "assessment.ir.hasDebt": "Has Debt",
      "assessment.ir.debtTypes": "Debt Types",
      "assessment.ir.overdue": "What's your company's total outstanding or overdue payables?",
      "assessment.ir.stability": "How stable and predictable is your revenue stream?",
      "assessment.ir.teamStrengths": "What best describes your team's primary strengths?",

      // Company Profile
      "company.name": "Company Name [legal] — *As per trade license*",
      "company.brand": "Brand/Operating Name (optional)",
      "company.incDate": "Incorporation Date",
      "company.address": "Registered Address",
      "company.website": "Website",
      "company.desc": "One-line Description (140 char)",
      "company.longDesc": "Long Description (1-2 paragraph)",
      "company.sector": "Sector/Industry",
      "company.team": "Founders & Key Team (names, role, LinkedIn)",
      "company.noData": "No company profile data submitted yet.",

      // Financials
      "financials.revenue": "Revenue (ITM – trailing 12 months):",
      "financials.ebitda": "EBITDA (Earnings Before Interest, Taxes, Depreciation & Amortization):",
      "financials.cash": "Cash on Hand:",
      "financials.equity": "Total Equity:",
      "financials.mrr": "Recurring Monthly Revenue (MRR):",
      "financials.noData": "No financial data submitted yet.",

      // KPIs
      "kpis.unitEconomics": "Unit Economics",
      "kpis.retention": "Retention & Churn",
      "kpis.growth": "Growth & Efficiency",
      "kpis.liquidity": "Liquidity & Runway",
      "kpis.noData": "No KPI data submitted.",

      // Data Room
      "dataRoom.category.Pitch Materials": "Pitch Materials",
      "dataRoom.category.Financials": "Financials",
      "dataRoom.category.Legal": "Legal",
      "dataRoom.category.Product": "Product",
      "dataRoom.file": "file",
      "dataRoom.files": "files",

      // Sidebar
      "sidebar.dashboard": "Dashboard",
      "sidebar.company": "Company",
      "sidebar.profile": "Profile",
      "sidebar.dataRoom": "Data Room",
      "sidebar.financials": "Financials",
      "sidebar.irScore": "IR Score",
      "sidebar.kpis": "KPIs",
      "sidebar.services": "Services",
      "sidebar.referAndEarn": "Refer and Earn",
      "sidebar.settings": "Settings",
      "sidebar.signOut": "Sign Out",
      "sidebar.financialHealth": "Financial Health",
      "sidebar.articles": "Articles",
      "sidebar.notifications": "Notifications",
      "sidebar.messageAdmin": "Message Admin",

      // User Profile Menu
      "userProfile.profileSettings": "Profile Settings",
      "userProfile.language": "Language",
      "userProfile.analytics": "Analytics",

      // Profile Page
      "profile.title": "Profile",
      "profile.breadcrumb.myApplications": "My Applications",
      "profile.yourInformation": "Your Information",
      "profile.fullName": "Full Name",
      "profile.name": "Name",
      "profile.email": "Email",
      "profile.phone": "Phone",
      "profile.country": "Country",
      "profile.saveChanges": "Save Changes",
      "profile.saving": "Saving...",
      "profile.alert.loginFirst": "Please login first",
      "profile.alert.saveSuccess": "Profile saved successfully!",
      "profile.alert.saveFailed": "Failed to save. Please try again.",

      "sidebar.menu.dashboard": "Dashboard",
      "sidebar.menu.raisingRequests": "Raising Requests",
      "sidebar.menu.partners": "Partners",
      "sidebar.menu.admins": "Admins",
      "sidebar.menu.settings": "Settings",
      "sidebar.menu.inviteAdmin": "Invite Admin",
      "sidebar.menu.adminProfile": "My Profile",

      // Hero Section
      "hero.title": "Set up your business and get your books done —",
      "hero.subtitle": "Conncct is where founders set up, stay compliant, and get their accounting and tax filing handled the right way. And when you're ready to grow — a raise, a new market, CapEx — the team known for closing deals gets you investor-ready.",
      "hero.createAccount": "Create a Free Account",
      "hero.getDemo": "Get a Demo",
      "hero.ctaAccounting": "Start your accounting",
      "hero.ctaSetup": "Set up your business",
      "hero.quote": "Conncct made fundraising simple.",
      "hero.saraRamirez": "Sara Ramirez",
      "hero.founder": "Founder, Dubai",

      // Featured Section
      "featured.title": "Featured In",

      // All In One Platform
      "platform.badge": "All In One Platform",
      "platform.title": "Everything you need to raise capital and scale your business, in one place.",

      // Assessment Section
      "assessment.badge": "Assessment",
      "assessment.title": "Investor Readiness Score",
      "assessment.description": "Get a personalized assessment of your startup's readiness to approach investors.",
      "assessment.cta": "Start Assessment",

      // Tools Section
      "tools.badge": "Tools",
      "tools.title": "Data Room & Tools",
      "tools.description": "All essential documents and templates in one place. Build your pitch deck, financial model, and more.",
      "tools.cta": "Explore Tools",

      // Consulting Section
      "consulting.badge": "Consulting",
      "consulting.title": "Expert Consulting",
      "consulting.description": "Get matched with advisors who can help you refine strategy, financials, and pitching.",
      "consulting.cta": "Book Consultation",

      // Free Tools Section
      "freeTools.title": "Free Tools to Get Started",
      "freeTools.subtitle": "Access our collection of free resources to kickstart your fundraising journey",
      "freeTools.card1.title": "Startup Pitch Deck Template",
      "freeTools.card1.description": "Professional template to present your vision",
      "freeTools.card1.cta": "Download Free",
      "freeTools.card2.title": "Financial Model Template",
      "freeTools.card2.description": "Excel template for projections",
      "freeTools.card2.cta": "Download Free",
      "freeTools.card3.title": "Cap Table Calculator",
      "freeTools.card3.description": "Track equity distribution easily",
      "freeTools.card3.cta": "Use Tool",

      // How It Works
      "howItWorks.title": "How It Works",
      "howItWorks.subtitle": "Three simple steps to get investor-ready",
      "howItWorks.step1.number": "01",
      "howItWorks.step1.title": "Take Assessment",
      "howItWorks.step1.description": "Complete our comprehensive startup readiness assessment to identify your strengths and gaps.",
      "howItWorks.step2.number": "02",
      "howItWorks.step2.title": "Access Tools",
      "howItWorks.step2.description": "Use our templates and tools to build your pitch deck, financial model, and data room.",
      "howItWorks.step3.number": "03",
      "howItWorks.step3.title": "Get Expert Help",
      "howItWorks.step3.description": "Connect with advisors who can refine your strategy and prepare you for investor meetings.",

      // Testimonials
      "testimonials.title": "Trusted by Founders Across the Region",
      "testimonials.subtitle": "See how Conncct has helped startups raise capital and grow",
      "testimonials.test1.quote": "Everything was in one place — from legal templates to investor outreach. Conncct saved us months of work.",
      "testimonials.test1.name": "Lydia Lopez",
      "testimonials.test1.title": "Evoleft - Completed pre-seed documentation in 10 days",
      "testimonials.test2.quote": "From day one, Conncct made us feel ready to pitch. The readiness score gave our investors real confidence.",
      "testimonials.test2.name": "Lina Farouk",
      "testimonials.test2.title": "BrightPay - Now expanding operations across UAE and KSA",
      "testimonials.test3.quote": "Conncct's advisors felt like part of our team. Their insights on KPIs and fundraising were game-changing.",
      "testimonials.test3.name": "Faisal Al Mansouri",
      "testimonials.test3.title": "Skillvise - Closed $850K bridge round after 3 months of consulting",
      "testimonials.test4.quote": "Conncct made fundraising simple — within weeks, we had our investor-ready data room and pitch deck ready by advisors.",
      "testimonials.test4.name": "Sara Haddad",
      "testimonials.test4.title": "Elevate - Raised $2M seed round within 45 days",
      "testimonials.test5.quote": "The consulting team refined our financial model and valuation. We finally understood what investors look for.",
      "testimonials.test5.name": "Omar Saleh",
      "testimonials.test5.title": "Foodbridge AI - Projected valuation by 15% and reduced strategic errors",

      // FAQ Section
      "faq.title": "Frequently Asked Questions",
      "faq.subtitle": "Everything you need to know about Conncct",

      // Footer
      "footer.description": "The all-in-one platform for startups to become investor-ready and raise capital.",
      "footer.company": "Company",
      "footer.aboutUs": "About Us",
      "footer.careers": "Careers",
      "footer.contact": "Contact",
      "footer.resources": "Resources",
      "footer.blog": "Blog",
      "footer.legal": "Legal",
      "footer.privacy": "Privacy Policy",
      "footer.support": "Support",
      "footer.allRightsReserved": "© 2025 Conncct. All rights reserved.",

      // Product Suite Section
      "productSuite.badge": "Product Suite",
      "productSuite.title": "The All In One Financial Platform for Founders like You.",
      "productSuite.card1.title": "Know Your Readiness",
      "productSuite.card1.description": "Easy to know how ready you are to raise capital. Track your investor-readiness score instantly.",
      "productSuite.card1.cta": "Track your score",
      "productSuite.card2.title": "Data Room",
      "productSuite.card2.description": "Keep all your documents in one place — safe, organized, and investor-ready.",
      "productSuite.card2.cta": "Access your documents",
      "productSuite.card3.title": "KPI Dashboard",
      "productSuite.card3.description": "Have all your key metrics that venture capital and angel investors are looking for.",
      "productSuite.card3.cta": "View your KPIs",
      "productSuite.expertCard.badge": "Expert Guidance",
      "productSuite.expertCard.title": "Get advice from seasoned experts — accounting, fractional CFOs, and legal advisors on demand.",
      "productSuite.expertCard.cta": "Talk to an expert",

      // Why Conncct Section
      "whyConncct.badge": "Resources",
      "whyConncct.title": "Why Conncct?",
      "whyConncct.subtitle": "We don't just connect entrepreneurs with advisors — we help you raise smarter, scale faster, and stay investor-ready.",
      "whyConncct.subtitle2": "See how working with a Conncct Consultant compares to doing it solo",
      "whyConncct.entrepreneursCard.title": "We've built startups, pitched investors, and scaled products ourselves.",
      "whyConncct.entrepreneursCard.cta": "Entrepreneurs",
      "whyConncct.consultantsCard.title": "We bring structure, strategy, and investor-readiness to your journey.",
      "whyConncct.consultantsCard.cta": "Consultants",

      // Built For Founders Section
      "builtForFounders.badge": "10,000+ Founders",
      "builtForFounders.title": "Built for Founders. Loved by Founders.",

      // FAQ Page
      "faq.heading": "FAQs",
      "faq.description": "Frequently asked questions ordered by popularity. Remember that if the visitor has not committed to the call to action, they may still have questions (doubts) that can be answered.",
      "faq.q1.question": "What is Conncct?",
      "faq.q1.answer": "Conncct is an all-in-one platform that helps startups and SMEs prepare for fundraising, manage their data rooms, and access expert consulting across finance, legal, and strategy — all in one place.",
      "faq.q2.question": "Do I need to be an established business to use Conncct?",
      "faq.q2.answer": "No, you don't. Conncct works with both early-stage startups and growing SMEs. Whether you're raising your first seed round or scaling for Series A, our consultants can help you get investor-ready.",
      "faq.q3.question": "Why should I use Conncct to raise capital?",
      "faq.q3.answer": "Conncct brings everything investors want under one roof — your pitch deck, valuation, KPIs, and financials — while connecting you with verified investors and advisors.",
      "faq.q4.question": "What information do you need from me to get started?",
      "faq.q4.answer": "We just need your company name, business stage, and funding goals. Once you sign up, our onboarding form will guide you through creating your readiness profile and setting up your data room.",
      "faq.q5.question": "What is Conncct Consulting?",
      "faq.q5.answer": "Conncct Consulting gives you access to experienced CFOs, legal advisors, and business strategists who tailor solutions to your growth goals — from refining your business model to improving your investor readiness.",
      "faq.q6.question": "Can Conncct help me with my business plan and valuation?",
      "faq.q6.answer": "Absolutely. Our team helps you build a detailed business plan, financial forecast, and valuation model aligned with investor expectations in your industry.",
      "faq.q7.question": "Who is Conncct designed for?",
      "faq.q7.answer": "Conncct is made for founders, entrepreneurs, and small business owners who want to raise capital efficiently, stay compliant, and scale strategically.",
      "faq.q8.question": "Is my data safe on Conncct?",
      "faq.q8.answer": "Yes. All your business and investor data is encrypted and stored in your secure data room, accessible only to you and approved partners.",

      // Login Page
      "login.title": "Welcome Back",
      "login.subtitle": "Sign in to your account to continue",
      "login.email": "Email",
      "login.password": "Password",

      // Admin Raising Requests
      "admin.raising.clientOverview": "Clients overview",
      "admin.raising.newClients": "New Clients",
      "admin.raising.last30Days": "Last 30 days",
      "admin.raising.allClients": "All Clients",
      "admin.raising.totalRequests": "Total requests",
      "admin.raising.searchPlaceholder": "Search by Account Number, Email, Phone or Name",
      "admin.raising.loading": "Loading raising requests...",
      "admin.raising.fastFilters": "Fast Filters",
      "admin.raising.newRequests": "New Requests",
      "admin.raising.applyFilters": "Apply Filters",
      "admin.raising.referralTable": "Referral Table",
      "admin.raising.showingRange": "Showing {{count}} of {{total}} raising requests",
      "admin.raising.refresh": "Refresh",
      "admin.raising.noRequests": "No raising requests match the current filters.",
      "admin.raising.unnamedFounder": "Unnamed Founder",
      "admin.raising.productLabelFallback": "Raising",
      "admin.raising.placeholder.startDate": "Start Date",
      "admin.raising.placeholder.endDate": "End Date",
      "admin.raising.placeholder.allCountries": "All Countries",
      "admin.raising.view": "View",

      // Admin Profile
      "admin.profile.title": "Your Information",
      "admin.profile.fullName": "Full Name",
      "admin.profile.email": "Email",
      "admin.profile.accountStatus": "Account Status:",
      "admin.profile.deleteAccount": "Delete Account",
      "admin.profile.deleteConfirm": "Are you sure you want to delete your account? This action cannot be undone.",

      // Admin Settings
      "admin.settings.title": "Admin Settings",
      "admin.settings.changePassword": "Change Password",
      "admin.settings.updatePasswordDesc": "Update your admin account password.",
      "admin.settings.currentPassword": "Current Password",
      "admin.settings.newPassword": "New Password",
      "admin.settings.currentPasswordPlaceholder": "Enter current password",
      "admin.settings.newPasswordPlaceholder": "Enter new password",
      "admin.settings.cancel": "Cancel",
      "admin.settings.update": "Update Password",
      "admin.settings.updating": "Updating...",
      "admin.settings.error.fillAll": "Please fill in all password fields",
      "admin.settings.error.minChars": "New password must be at least 6 characters",
      "admin.settings.success": "Password changed successfully! Logging out...",
      "admin.settings.error.failed": "Failed to change password",
      "admin.settings.error.generic": "Error changing password. Please try again.",

      // Admin Invitation
      "admin.invitation.sendTitle": "Send Admin Invite",
      "admin.invitation.nameLabel": "Admin Name",
      "admin.invitation.namePlaceholder": "Enter admin name",
      "admin.invitation.emailLabel": "Admin Email",
      "admin.invitation.emailPlaceholder": "Enter email address",
      "admin.invitation.note": "Note: A secure password will be automatically generated and sent to the admin's email address. The admin can login immediately with the provided credentials.",
      "admin.invitation.sendButton": "Send Invitation",
      "admin.invitation.restrictedTitle": "Restricted Access",
      "admin.invitation.restrictedDesc": "Only the Super Admin can send invitations.",
      "admin.invitation.adminsTitle": "Admins",
      "admin.invitation.adminsCount": "Admins",
      "admin.invitation.tableTitle": "Invitation Table",
      "admin.invitation.header.name": "Name",
      "admin.invitation.header.email": "Email",
      "admin.invitation.header.invitedOn": "Invited On",
      "admin.invitation.header.status": "Status",
      "admin.invitation.header.action": "Action",
      "admin.invitation.loading": "Loading...",
      "admin.invitation.noAdmins": "No admins found",
      "admin.invitation.status.invited": "Invited",
      "admin.invitation.status.active": "Active",
      "admin.invitation.delete": "Delete",
      "admin.invitation.alert.enterName": "Please enter the admin name",
      "admin.invitation.alert.enterEmail": "Please enter a valid email address",
      "admin.invitation.alert.sentSuccess": "Admin invitation sent successfully!",
      "admin.invitation.alert.sentEmail": "An email has been sent to {{email}} with login credentials.",
      "admin.invitation.alert.deleteConfirm": "Are you sure you want to remove admin \"{{name}}\"? This action cannot be undone.",
      "admin.invitation.alert.deleteSuccess": "Admin removed successfully",
      "admin.invitation.alert.deleteFail": "Failed to remove admin",

      // Login Page (existing)
      "login.welcomeTo": "Welcome to",
      "login.portalName": "the Conncct Portal",
      "login.welcomeSubtitle": "Access your startup tools, diagnostics, and reports all in one place.",
      "login.signInTitle": "Sign In to Your Account",
      "login.signInSubtitle": "Enter your credentials to access your dashboard.",
      "login.emailPlaceholder": "Enter Your Email Address",
      "login.passwordPlaceholder": "Enter Your Password",
      "login.rememberMe": "Remember me",
      "login.forgotPassword": "Forgot Password",
      "login.signIn": "Sign In",
      "login.signingIn": "Signing In...",
      "login.orContinueWith": "Or continue with",
      "login.dontHaveAccount": "Don't have an account? Create one",

      // Onboarding Guard
      "onboardingGuard.redirecting": "Redirecting you to your Dashboard",

      // Signup Page
      "signup.title": "Create Account",
      "signup.subtitle": "Join Conncct to get investor-ready",
      "signup.howCanHelp": "How can we help you?",
      "signup.investorReadinessTitle": "Investor Readiness & Capital",
      "signup.investorReadinessDesc": "For startups preparing to fundraise.",
      "signup.raiseCapitalTitle": "Raise Capital",
      "signup.raiseCapitalDesc": "For startups and businesses who want to raise capital",
      "signup.businessSetupTitle": "Business Setup & Growth",
      "signup.businessSetupDesc": "Access startup formation, compliance, and advisory support to set up and scale your business.",
      "signup.continue": "Continue",
      "signup.yourJourney": "Your Founder Journey",
      "signup.startsHere": "Starts Here",
      "signup.accessTools": "Access tools, assessments, and consulting to help you grow and raise capital.",
      "signup.firstName": "First Name",
      "signup.lastName": "Last Name",
      "signup.fullName": "Full Name",
      "signup.phoneNumber": "Enter Phone Number",
      "signup.email": "Enter Email",
      "signup.password": "Enter Password",
      "signup.confirmPassword": "Enter Confirm Password",
      "signup.createAccount": "Create My Account",
      "signup.haveAccount": "Already have an account?",
      "signup.signIn": "Log in",
      "signup.or": "or",
      "signup.continueWithGoogle": "Continue with Google",
      "signup.back": "Back",
      "signup.fillAllFields": "Please fill in all fields",
      "signup.passwordMismatch": "Passwords do not match",
      "signup.passwordTooShort": "Password must be at least 6 characters",
      "signup.invalidEmail": "Please enter a valid email",
      "signup.creatingAccount": "Creating account...",
      "signup.howCanHelpToday": "How can we help you today?",
      "signup.selectService": "Select the service that best fits your current business stage. We'll guide you through the rest.",
      "signup.setupGrowthTitle": "Setup & Growth",
      "signup.setupGrowthSubtitle": "You're building the company. We build the foundation.",
      "signup.setupGrowthFeature1": "Incorporated where it matters most — UAE, USA & global hubs",
      "signup.setupGrowthFeature2": "Corporate Tax, VAT & filings — fully managed",
      "signup.setupGrowthFeature3": "Clean books & reporting, ready for any investor",
      "signup.setupGrowthCTA": "Set Up My Company",
      "signup.investorReadinessCardTitle": "Investor Readiness",
      "signup.investorReadinessSubtitle": "You have a company. Now make it one investor can't ignore.",
      "signup.investorReadinessFeature1": "Financials that hold up under VC scrutiny",
      "signup.investorReadinessFeature2": "A pitch deck built to get second meetings",
      "signup.investorReadinessFeature3": "All document investors want — ready before they ask",
      "signup.investorReadinessCTA": "Become Fundable",
      "signup.capitalRaisingTitle": "Capital Raising",
      "signup.capitalRaisingSubtitle": "You're ready to raise. We raise with you.",
      "signup.capitalRaisingFeature1": "A data room investors open",
      "signup.capitalRaisingFeature2": "Matched with VCs, angels & family offices who fit",
      "signup.capitalRaisingFeature3": "By your side — from first meeting to term sheet",
      "signup.capitalRaisingCTA": "Launch My Raise",

      // Capital flow stepper labels
      "capitalFlow.step1": "Goal Assessment",
      "capitalFlow.step2": "Investor Readiness",
      "capitalFlow.step3": "Capital Assessment",

      // Capital Assessment Page
      "capitalAssessment.sectionLabel": "Capital Raising",
      "capitalAssessment.q1": "What stage is your company at?",
      "capitalAssessment.q1opt1": "Pre-revenue / Pre-product",
      "capitalAssessment.q1opt1sub": "Idea / early MVP",
      "capitalAssessment.q1opt2": "MVP / Early traction",
      "capitalAssessment.q1opt2sub": "Building momentum",
      "capitalAssessment.q1opt3": "Revenue generating",
      "capitalAssessment.q1opt3sub": "Customers paying",
      "capitalAssessment.q1opt4": "Scaling / Growth stage",
      "capitalAssessment.q1opt4sub": "Growing fast",
      "capitalAssessment.q2": "How much are you looking to raise?",
      "capitalAssessment.q2opt1": "Under $500K",
      "capitalAssessment.q2opt2": "$500K – $2M",
      "capitalAssessment.q2opt3": "$2M – $10M",
      "capitalAssessment.q2opt4": "$10M+",
      "capitalAssessment.q3": "What type of funding are you seeking?",
      "capitalAssessment.q3opt1": "Equity (VC / Angel)",
      "capitalAssessment.q3opt2": "Convertible Note / SAFE",
      "capitalAssessment.q3opt3": "Debt financing",
      "capitalAssessment.q3opt4": "Not decided yet",
      "capitalAssessment.q4": "Where are you in your fundraising process?",
      "capitalAssessment.q4opt1": "Just starting out",
      "capitalAssessment.q4opt2": "Building pitch materials",
      "capitalAssessment.q4opt3": "Actively meeting investors",
      "capitalAssessment.q4opt4": "Closing a round",
      "capitalAssessment.q5": "What's your biggest fundraising challenge?",
      "capitalAssessment.q5opt1": "Finding the right investors",
      "capitalAssessment.q5opt2": "Valuation & deal terms",
      "capitalAssessment.q5opt3": "Pitch deck & storytelling",
      "capitalAssessment.q5opt4": "Due diligence & financials",

      // Country Selection Page
      "country.selectCountry": "Select Your Country",
      "country.chooseLocation": "Choose your location to get started",
      "country.continue": "Continue",
      "country.selectOne": "Please select a country",

      // About Page
      "about.hero": "Let's Conncct You!",
      "about.visionBadge": "Our Vision",
      "about.vision": "A world where fundraising is structured, transparent, and data-driven — giving every ambitious founder a clear and repeatable path to capital and growth.",
      "about.missionBadge": "Our Mission",
      "about.mission": "To equip founders and entrepreneurs, especially across emerging markets, with the tools, insights, and expert support needed to become investor-ready, close funding rounds faster, and build companies that scale with discipline.",
      "about.whoBadge": "Who We Are",
      "about.who1": "Conncct is a capital-readiness and fundraising platform built on global standards and grounded in regional insight.",
      "about.who2": "We help founders avoid costly mistakes, build investor-grade materials, and navigate the fundraising process with structure, clarity, and speed.",
      "about.who3": "Our work sits at the intersection of finance, technology, and human expertise — transforming how startups, investors, and consultants collaborate to grow sustainable businesses.",
      "about.cultureBadge": "Our Culture",
      "about.culture1": "We believe that meaningful change happens when strategy meets passion.",
      "about.culture2": "Innovation",
      "about.culture2Text": "We use data and AI to challenge the traditional consulting model.",
      "about.culture3": "Collaboration",
      "about.culture3Text": "We work across industries, geographies, and ideas.",
      "about.culture4": "Integrity",
      "about.culture4Text": "We believe in transparency and accountability — inside and outside the company.",
      "about.culture5": "Impact",
      "about.culture5Text": "Every project we deliver helps founders grow and investors invest smarter.",
      "about.manifestoTitle": "Conncct Manifesto",
      "about.manifesto1": "Welcome to the age of the \"Builder.\"",
      "about.manifesto2": "Builders don't just dream.",
      "about.manifesto3": "Builders design.",
      "about.manifesto4": "Builders execute.",
      "about.manifesto5": "Builders raise.",
      "about.manifesto6": "Builders are the new generation of founders — disciplined, data-driven, and daring enough to turn bold ideas into investable realities.",
      "about.manifesto7": "Never before has capital been so close.",
      "about.manifesto8": "Never before have tools, insights, and global networks been so accessible.",
      "about.manifesto9": "Never before have founders been so empowered to build with clarity — not chaos.",
      "about.manifesto10": "Technology is rewriting the rules of fundraising.",
      "about.manifesto11": "And Conncct is where ambition meets structure.",
      "about.manifesto12": "Do your game-changing startup.",
      "about.manifesto13": "Do your next funding round.",
      "about.manifesto14": "Do your investor-ready story.",
      "about.manifesto15": "Do your disciplined growth.",
      "about.manifesto16": "Now more than ever, this is the time of the Builder.",
      "about.manifesto17": "And Conncct helps Builders across emerging markets raise smart, scale fast, and stay focused.",
      "about.manifesto18": "That's our mission.",
      "about.manifesto19": "Conncct democratizes access to capital-readiness — transforming how founders, investors, and consultants collaborate to grow sustainable businesses.",
      "about.manifesto20": "We invite Builders around the world to stop guessing — and start growing.",
      "about.manifesto21": "Together, there's nothing we can't build.",
      "about.manifesto22": "Let's Conncct You!",
      "about.ctaTitle": "Your next chapter starts here.",
      "about.ctaText": "At Conncct, we help shape how founders raise, investors invest, and startups scale — across emerging markets and beyond. We're building the future of capital.",
      "about.ctaButton": "Join Now!",

      // Pricing Page
      "pricing.title": "Pricing",
      "pricing.subtitle": "Choose the perfect plan for your fundraising journey",
      "pricing.billingToggle": "Billing Cycle",
      "pricing.yearly": "Yearly",
      "pricing.monthly": "Monthly",
      "pricing.unlimited": "Unlimited",
      "pricing.starterPack": "Starter Pack",
      "pricing.starterDuration": "2-3 weeks",
      "pricing.starterTag": "Investor Essentials",
      "pricing.starterDesc": "For early-stage founders who ned to get the basic right",
      "pricing.deliverables": "Deliverables",
      "pricing.getStarted": "Get Started",
      "pricing.selectPlan": "Please select a plan",

      // Starter Pack - New Keys
      "pricing.starter.name": "Starter Pack",
      "pricing.starter.duration": "2-3 weeks",
      "pricing.starter.badge": "Investor Essentials",
      "pricing.starter.desc": "For early-stage founders who need to get the basics right",
      "pricing.starter.deliverables": "Deliverables",
      "pricing.starter.price": "$2,999",

      // Growth Pack - New Keys
      "pricing.growth.name": "Growth Pack",
      "pricing.growth.duration": "4-8 weeks",
      "pricing.growth.badge": "Investment-Ready",
      "pricing.growth.desc": "For startups preparing for seed/Series A fundraising.",
      "pricing.growth.price": "$6,999",

      // Scale Pack (Pro) - New Keys
      "pricing.pro.name": "Scale Pack",
      "pricing.pro.duration": "8-12 weeks",
      "pricing.pro.badge": "Full Due Diligence Box",
      "pricing.pro.desc": "For scale-ups targeting institutional investors, VCs, or expansion funding",
      "pricing.pro.price": "$11,999",
      "pricing.pro.item1": "Pitch Deck (designed + customized)",
      "pricing.pro.item2": "Advanced Financial Model",
      "pricing.pro.item3": "Comprehensive Business Plan",
      "pricing.pro.item4": "Company Valuation Report",

      // Starter Pack Deliverables
      "pricing.starter.item1": "Pitch Deck(10-12 slides,investor-focuse)",
      "pricing.starter.item2": "Finicial Plan (3-year projections,driver-based model)",
      "pricing.starter.item3": "Business Plan (Lite: Lean Canvas + narrative brief, ~8–10 pages)",


      // Growth Pack Deliverables
      "pricing.growth.item1": "Pitch Deck (investor-ready, financials integrated)",
      "pricing.growth.item2": "Financial Model (5-year, scenario-based, CAPEX/OPEX, KPIs)",
      "pricing.growth.item3": "Business Plan (Full version: strategy, market, financials, ~10 pages)",
      "pricing.growth.item4": "Company Valuation Report (DCF, comparables, multiples)",
      "pricing.growth.item5": "Investor Data Room Setup (folders,financial statements, due diligence docs checklist)",


      // Scale Pack Deliverables
      "pricing.scale.price": "$11,999",
      "pricing.scale.oldPrice": "$13,999",
      "pricing.scale.item1": "Pitch Deck (designed + customized for different investor types)",
      "pricing.scale.item2": "Advanced Financial Model (multi-scenario, price × volume, regional expansion)",
      "pricing.scale.item3": "Comprehensive Business Plan (in-depth, 40+ pages, funding strategy included)",
      "pricing.scale.item4": "Company Valuation Report (multi-method, sensitivity analysis",
      "pricing.scale.item5": "Full Investor Data Room (financials, policies, KPIs, governance docs, legal compliance",
      "pricing.fourPayments": "Four Payments | Annual",
      "pricing.save": "Save $2,000*",
      "pricing.back": "Back",
      "pricing.next": "Next",
      "pricing.modal.title": "Feeling Stuck?",
      "pricing.modal.description": "Book a free demo call today and let our experts guide you.",
      "pricing.modal.continue": "Continue",
      "pricing.modal.bookDemo": "Book A Demo",
      "pricing.scale.item6": "Due Diligence Readiness Review (gap analysis + corrective plan",
      "pricing.scale.savings": "$3,999/ Four Payments (Annual)",

      "pricing.growthPack": "Growth Pack",
      "pricing.growthDuration": "4-8 weeks",
      "pricing.growthTag": "Investment-Ready",
      "pricing.growthDesc": "For startups preparing for seed/Series A fundraising.",
      "pricing.scalePack": "Scale Pack",
      "pricing.scaleDuration": "8-12 weeks",
      "pricing.scaleTag": "Full Due Diligence Box",
      "pricing.scaleDesc": "For scale-ups targeting institutional investors, VCs, or expansion funding",
      "pricing.mostPopular": "Most Popular",
      "pricing.selected": "Selected",
      "pricing.continue": "Continue to Payment",
      "pricing.limit.100": "100",
      "pricing.featureTable": "Feature Table",
      "pricing.featureTableSubtitle": "Choose the perfect plan for your business needs",
      "pricing.taskManagement": "Task Management",
      "pricing.customWorkflows": "Customizable Workflows",
      "pricing.projectPlanning": "Project Planning",
      "pricing.timeTracking": "Time Tracking",
      "pricing.visualization": "Visualization",
      "pricing.ganttCharts": "Gantt Charts",
      "pricing.agileSupport": "Agile Methodology Support",
      "pricing.reportingAnalytics": "Reporting and Analytics",
      "pricing.teamDashboards": "Team and Individual Dashboards",
      "pricing.integrations": "Integrations",
      "pricing.docManagement": "Document Management",
      "pricing.clientCollaboration": "Client Collaboration",
      "pricing.mobileApp": "Mobile App Integration",
      "pricing.faqTitle": "FAQs",
      "pricing.faqSubtitle": "Frequently asked questions ordered by popularity. Remember that if the visitor has not committed to the call to action, they may still have questions (doubts) that can be answered.",
      "pricing.faqQ1": "What is Conncct?",
      "pricing.faqA1": "Conncct is an all-in-one platform that helps startups and SMEs prepare for fundraising, manage their data rooms, and access expert consulting across finance, legal, and strategy — all in one place.",
      "pricing.faqQ2": "Do I need to be an established business to use Conncct?",
      "pricing.faqA2": "No, you don't. Conncct works with both early-stage startups and growing SMEs. Whether you're raising your first seed round or scaling for Series A, our consultants can help you get investor-ready.",
      "pricing.faqQ3": "Why should I use Conncct to raise capital?",
      "pricing.faqA3": "Conncct brings everything investors want under one roof — your pitch deck, valuation, KPIs, and financials — while connecting you with verified investors and advisors.",
      "pricing.faqQ4": "What information do you need from me to get started?",
      "pricing.faqA4": "We just need your company name, business stage, and funding goals. Once you sign up, our onboarding form will guide you through creating your readiness profile and setting up your data room.",
      "pricing.faqQ5": "What is Conncct Consulting?",
      "pricing.faqA5": "Conncct Consulting gives you access to experienced CFOs, legal advisors, and business strategists who tailor solutions to your growth goals — from refining your business model to improving your investor readiness.",
      "pricing.faqQ6": "Can Conncct help me with my business plan and valuation?",
      "pricing.faqA6": "Absolutely. Our team helps you build a detailed business plan, financial forecast, and valuation model aligned with investor expectations in your industry.",
      "pricing.faqQ7": "Who is Conncct designed for?",
      "pricing.faqA7": "Conncct is made for founders, entrepreneurs, and small business owners who want to raise capital efficiently, stay compliant, and scale strategically.",
      "pricing.faqQ8": "Is my data safe on Conncct?",
      "pricing.faqA8": "Yes. All your business and investor data is encrypted and stored in your secure data room, accessible only to you and approved partners.",

      // Careers Page
      "careers.heroTitle": "Careers at Conncct",
      "careers.heroSubtitle": "Build the future of fundraising, strategy, and growth.",
      "careers.heroText1": "At Conncct, we're on a mission to redefine how founders raise capital and build enduring businesses.",
      "careers.heroText2": "We combine AI-powered insights, data-driven strategy, and expert consulting to help entrepreneurs become investor-ready, close rounds faster, and scale with confidence.",
      "careers.heroText3": "If you're passionate about innovation, technology, and empowering founders — this is the place to be.",
      "careers.viewRoles": "View Open Roles",
      "careers.lifeTitle": "Life at Conncct",
      "careers.lifeSubtitle": "We don't just work on startups — we work like one. Flexible, fast, and focused on growth.",
      "careers.card1": "Work remotely or from hubs in Dubai, Abu Dhabi, or London",
      "careers.card2": "Collaborate directly with founders, investors, and innovators",
      "careers.card3": "Learn from top industry experts and expand your skill set",
      "careers.card4": "Be part of a mission that drives real economic and social impact",
      "careers.departmentsTitle": "Departments We're Growing",
      "careers.departmentsText": "We're expanding our team across several key areas:",
      "careers.dept1Title": "Business & Strategy Consulting",
      "careers.dept1Text": " – Guide startups with strategy, market insight, and financial clarity.",
      "careers.dept2Title": "Finance & Investment Advisory",
      "careers.dept2Text": " – Design models, valuations, and investor materials that close rounds.",
      "careers.dept3Title": "Product & Technology",
      "careers.dept3Text": " – Build the AI systems and digital tools powering smarter fundraising.",
      "careers.dept4Title": "Marketing & Growth",
      "careers.dept4Text": " – Drive engagement through storytelling, brand strategy, and digital campaigns.",
      "careers.dept5Title": "Partnerships & Ecosystem",
      "careers.dept5Text": " – Collaborate with VCs, accelerators, and institutions to scale our impact.",
      "careers.valuesTitle": "Our Values",
      "careers.value1": "Clarity",
      "careers.value1Text": " — Data and discipline guide every decision.",
      "careers.value2": "Creativity",
      "careers.value2Text": " — We build smarter solutions through design and technology.",
      "careers.value3": "Community",
      "careers.value3Text": " — Together, we make the startup ecosystem stronger.",
      "careers.value4": "Commitment",
      "careers.value4Text": " — We go the extra mile for our founders — and for each other.",
      "careers.ctaTitle": "Something great starts here.",
      "careers.ctaText": "Join a team where your work creates measurable impact — for founders, investors, and the future of finance. Let's build a smarter, fairer fundraising ecosystem together.",
      "careers.ctaEmail": "Send your CV to careers@conncct.com",

      // Contact Page
      "contact.title": "Contact Us!",
      "contact.subtitle": "We'd love to hear from you. Whether you have a question, need support, or want to explore how Conncct can accelerate your business journey, our team is here to help.",
      "contact.officeTitle": "Office Address",
      "contact.address1": "Dubai Creek Harbour",
      "contact.address2": "South Tower 2, Office 1303",
      "contact.address3": "Dubai, United Arab Emirates",
      "contact.emailTitle": "Email",
      "contact.emailAddress": "support@conncct.com",
      "contact.phoneTitle": "Phone / WhatsApp",
      "contact.phoneNumber": "+971 50 164 1651",
      "contact.hoursTitle": "Business Hours",
      "contact.hours1": "Sunday – Friday: 9:00 AM – 7:00 PM (GST)",
      "contact.hours2": "Saturday: Closed",
      "contact.touchTitle": "Get In Touch",
      "contact.touchText1": "For partnership opportunities, investment inquiries, or media requests, please reach out through our email above.",
      "contact.touchText2": "Our team will respond within 24–48 hours.",

      // Raise Capital Page
      "raiseCapital.title": "Raise Capital",
      "raiseCapital.subtitle": "Your amount that you want to raise will guide us in finding the right investor.",
      "raiseCapital.q1.title": "How much are you looking to raise?",
      "raiseCapital.q1.subtitle": "Your amount that you want to raise will guide us in finding the right investor.",
      "raiseCapital.q1.placeholder": "Enter Your Amount Here",
      "raiseCapital.q2.title": "What is the Round that you are raising for?",
      "raiseCapital.q2.subtitle": "This helps us match you with the right investor profiles and check sizes.",
      "raiseCapital.q2.preSeed": "Pre-seed",
      "raiseCapital.q2.seed": "Seed",
      "raiseCapital.q2.seriesA": "Series A",
      "raiseCapital.q2.seriesB": "Series B+",
      "raiseCapital.q2.notSure": "Not sure yet",
      "raiseCapital.q3.title": "What type of capital are you raising?",
      "raiseCapital.q3.equity": "Equity (priced round)",
      "raiseCapital.q3.convertible": "Convertible (SAFE/Note)",
      "raiseCapital.q3.debt": "Debt",
      "raiseCapital.q3.notSure": "Not sure yet",
      "raiseCapital.q4.title": "Do you have a target valuation or cap? (optional)",
      "raiseCapital.q4.subtitle": "Sharing a target pre-money (or SAFE cap) helps us match you with the right investors and avoid misaligned conversations.",
      "raiseCapital.q4.placeholder": "Enter Your Amount Here",
      "raiseCapital.q5.title": "Tell us about your company",
      "raiseCapital.q5.subtitle": "We just need a few more details to proceed.",
      "raiseCapital.q5.placeholder": "Tell us more about your company",
      "raiseCapital.q6.placeholder": "your website",
      "raiseCapital.backToDashboard": "← Back to Dashboard",
      "raiseCapital.continue": "Continue",
      "raiseCapital.saving": "Saving...",
      "raiseCapital.alert.fillRequired": "Please fill in all required fields",
      "raiseCapital.alert.loginFirst": "Please login first",
      "raiseCapital.alert.saveFailed": "Failed to save. Please try again.",

      // Partner Page
      "partner.heroTitle": "Partner with",
      "partner.heroTitleBrand": "Conncct",
      "partner.heroText1": "Earn up to $5,000 per referral by introducing new startups, founders, and entrepreneurs to Conncct — the all-in-one platform for business setup, fundraising, and digital transformation.",
      "partner.heroText2": "Choose the partnership model that fits you best.",
      "partner.heroText3": "Refer Conncct. Start earning. It's really that easy.",
      "partner.becomePartner": "Become a Partner",
      "partner.commissionTitle": "Your Commissions",
      "partner.joinTitle": "Join hundreds earning monthly passive income as a Conncct partner",
      "partner.rightFitTitle": "Are you the right fit?",
      "partner.fitCard1Title": "Our best-fit partners support these ecosystems:",
      "partner.fitCard1Item1": "Women Entrepreneurs",
      "partner.fitCard1Item2": "Digital & Marketing Firms",
      "partner.fitCard1Item3": "Accountants & Financial Advisors",
      "partner.fitCard1Item4": "Startup Accelerators & Business Consultants",
      "partner.fitCard2Title": "Our best-fit partners want to grow their reach and are:",
      "partner.fitCard2Item1": "Content Creators & Educators",
      "partner.fitCard2Item2": "Community Builders & Influencers",
      "partner.fitCard2Item3": "Investment Advisors & Startup Mentors",
      "partner.earningTitle": "How does earning up to $5,000 per referral sound?",
      "partner.earningItem1": "Top partners generate $10,000+ monthly by referring founders, startups, and businesses to Conncct's platform.",
      "partner.earningItem2": "Earn up to 50% commissions — as much as $5,000 per successful referral.",
      "partner.earningItem3": "Collaborate directly with the Conncct leadership team to build growth pipelines and long-term partnerships.",
      "partner.earningQuote": "Join the Conncct Partner Network — where expertise meets opportunity.",
      "partner.stepsTitle": "Partner with",
      "partner.stepsSubtitle": "in 3 Simple Steps",
      "partner.step1Title": "Join the Conncct Partner Network",
      "partner.step1Text": "Become an official partner and help founders, advisors, and businesses discover Conncct.",
      "partner.step1Item1": "Early access to our community",
      "partner.step1Item2": "Stay updated on upcoming tools and partner features",
      "partner.step2Title": "Access Your Partner Kit",
      "partner.step2Text": "We'll send you all the essentials to get started.",
      "partner.step2Item1": "Pitch decks, visuals, and onboarding templates",
      "partner.step2Item2": "Guidelines on how to introduce Conncct to your network",
      "partner.step2Item3": "Email us at referral@conncct.com to receive your kit.",
      "partner.step3Title": "Earn and Grow",
      "partner.step3Subtitle": "(Referral Codes Coming Soon)",
      "partner.step3Text": "You'll be among the first to receive a unique partner code and dashboard once Phase 2 launches.",
      "partner.step3Item1": "Track referrals and performance",
      "partner.step3Item2": "Enjoy exclusive partner rewards and commissions",
      "partner.consultingTitle": "Promote Conncct's Consulting Services",
      "partner.consultingText": "As a Conncct partner, you can refer clients to our consulting solutions — helping startups and SMEs grow smarter while earning commissions along the way.",
      "partner.consultingCard1Badge": "Business",
      "partner.consultingCard1Title": "Business Strategy & Growth",
      "partner.consultingCard1Text": "We help founders shape scalable business models, refine go-to-market strategies, and set clear growth milestones — turning ideas into sustainable ventures.",
      "partner.consultingCard2Badge": "Financial",
      "partner.consultingCard2Title": "Financial Planning & Advisory",
      "partner.consultingCard2Text": "Conncct supports companies with budgeting, forecasting, and financial structuring — ensuring they're investor-ready and built for long-term success.",
      "partner.consultingCard3Badge": "Fundraising",
      "partner.consultingCard3Title": "Fundraising & Investor Readiness",
      "partner.consultingCard3Text": "We prepare startups for capital raising with tailored pitch decks, valuations, and access to our expanding investor network.",
      "partner.faqTitle": "FAQs",
      "partner.faqSubtitle": "Frequently asked questions ordered by popularity. Remember that if the visitor has not committed to the call to action, they may still have questions (doubts) that can be answered.",
      "partner.faqQ1": "What is the Conncct Partner Program?",
      "partner.faqQ2": "What are the ways to partner with Conncct?",
      "partner.faqQ3": "What kind of clients can I refer?",
      "partner.faqQ4": "What rewards do partners receive?",
      "partner.faqQ5": "How can I join?",
      "partner.faqA1.intro": "The Conncct Partner Program enables professionals — including consultants, accountants, accelerators, and advisors — to refer startups and businesses to our platform. Partners can connect clients to Conncct's consulting, financial, and digital solutions while earning commissions on each successful engagement.",
      "partner.faqA1.simplifies": "Conncct simplifies access to:",
      "partner.faqA1.bullet1": "Business setup and compliance advisory",
      "partner.faqA1.bullet2": "Financial planning and investor readiness consulting",
      "partner.faqA1.bullet3": "Digital transformation and marketing services (via Tech-X.co)",
      "partner.faqA2.intro": "You can partner with Conncct in two main ways:",
      "partner.faqA2.referral": "Referral Partner",
      "partner.faqA2.referralDesc": "— Introduce clients to Conncct and earn a commission per successful signup.",
      "partner.faqA2.consulting": "Consulting Partner (Coming Soon)",
      "partner.faqA2.consultingDesc": "— Wholesale pricing, co-branding opportunities, and access to client dashboards for white-label consulting and support.",
      "partner.faqA2.enterprise": "Enterprise and accelerator partners can request custom collaboration packages based on volume and market reach.",
      "partner.faqA3.intro": "Our ideal referrals include:",
      "partner.faqA3.bullet1": "Startups and SMEs seeking consulting or fundraising support",
      "partner.faqA3.bullet2": "Founders preparing investor materials or financial plans",
      "partner.faqA3.bullet3": "Businesses pursuing digital or operational transformation",
      "partner.faqA4": "Earn up to 50% commissions and unlock exclusive partner benefits as the program grows — including co-marketing, event invitations, and early access to new tools.",
      "partner.faqA5": "Email us at referral@conncct.com to get started.",
      "partner.ctaTitle": "Let's grow and earn together",
      "partner.ctaText1": "Become a partner with Conncct and help founders, startups, and businesses grow smarter — while earning along the way.",
      "partner.ctaText2": "Join a global network of consultants, advisors, and professionals shaping the future of business and finance.",
      "partner.ctaButton": "Become a Partner Talk to Sales",

      // Demo Pages
      "demo.login": "Login",
      "demo.title1": "Let's Walk You Through Connct, It's Pretty Cool.",
      "demo.tag1": "Google Meet or Zoom",
      "demo.tag2": "15 Min",
      "demo.tag3": "Free",
      "demo.firstName": "First Name",
      "demo.lastName": "Last Name",
      "demo.email": "Enter Email",
      "demo.terms": "By continuing, I agree to Connct's",
      "demo.termsLink": "Terms & Conditions",
      "demo.next": "Next →",
      "demo.back": "← Back",
      "demo.title2": "We're Excited to Show You the Platform!",
      "demo.helpWith": "What Do You Need Help With?",
      "demo.helpOption1": "Raising Capital",
      "demo.helpOption2": "Building a Business Plan",
      "demo.helpOption3": "Improving IR Score",
      "demo.helpOption4": "Financial Consulting",
      "demo.companyName": "Company Name",
      "demo.discussionTopic": "What do you want to discuss?",
      "demo.submit": "Done →",
      "demo.submitting": "Submitting...",
      "demo.fillAllFields": "Please fill in all fields",
      "demo.missingInfo": "Missing information from previous step",
      "demo.cannotConnect": "Cannot connect to server. Please check if the backend is running.",

      // Goal Assessment Form
      "goal.step1": "Goal Assessment",
      "goal.step2": "Investor Readiness Score",
      "goal.step3": "Explore Tools",
      "goal.q1": "What's your #1 focus right now?",
      "goal.q1opt1": "Raise Funds",
      "goal.q1opt2": "Build Product",
      "goal.q1opt3": "Grow Sales",
      "goal.q1opt4": "Setup Ops",
      "goal.q2": "Where does most of your time go?",
      "goal.q2opt1": "Investor Work",
      "goal.q2opt2": "Product Build",
      "goal.q2opt3": "Sales Marketing",
      "goal.q2opt4": "Ops Legal",
      "goal.q3": "What would a 6-month win look like for you?",
      "goal.q3opt1": "Funded Round",
      "goal.q3opt2": "Live Product",
      "goal.q3opt3": "Revenue Growth",
      "goal.q3opt4": "Clean Ops",
      "goal.q4": "What's holding you back most today?",
      "goal.q4opt1": "Investor Knowhow",
      "goal.q4opt2": "Build Test",
      "goal.q4opt3": "Customer Growth",
      "goal.q4opt4": "Legal Setup",
      "goal.q5": "What's one step you can take this week?",
      "goal.q5opt1": "Fundraise Now",
      "goal.q5opt2": "Build Features",
      "goal.q5opt3": "Launch Marketing",
      "goal.q5opt4": "Hire COO",
      "goal.back": "← Back",
      "goal.continue": "Continue →",
      "goal.saving": "Saving...",
      "goal.modalTitle": "Are you Ready to Unlock your Investor Readiness Score Now?",
      "goal.modalYes": "Yes",
      "goal.modalYesDesc": "It will increase your chance to increase capital",
      "goal.modalNo": "No",
      "goal.modalNoDesc": "You can take it later from the dashboard",
      "goal.modalNext": "Next",
      "goal.alertAllQuestions": "Answer all the questions",
      "goal.alertFillAll": "Answer all the questions",
      "goal.alertLogin": "Please login first",

      // Financials
      "financials.title": "Financials",
      "financials.myApplications": "My Applications",
      "financials.yourInfo": "Your Financial Information",
      "financials.infoText": "These figures help us benchmark your company's financial readiness and compare it with investor expectations.",
      "financials.revenueLabel": "Revenue (ITM – trailing 12 months):",
      "financials.revenuePlaceholder": "Revenue (ITM – trailing 12 months)",
      "financials.ebitdaLabel": "EBITDA (Earnings Before Interest, Taxes, Depreciation & Amortization):",
      "financials.ebitdaPlaceholder": "EBITDA (Earnings Before Interest, Taxes, Depreciation & Amortization)",
      "financials.cashOnHandLabel": "Cash on Hand:",
      "financials.cashOnHandPlaceholder": "Cash on Hand",
      "financials.totalEquityLabel": "Total Equity",
      "financials.totalEquityPlaceholder": "Total Equity",
      "financials.mrrLabel": "Recurring Monthly Revenue (MRR):",
      "financials.mrrPlaceholder": "Recurring Monthly Revenue (MRR)",
      "financials.saveButton": "Save Financial Data",
      "financials.saving": "Saving...",
      "financials.alert.login": "Please login to save financial data",
      "financials.alert.emailNotFound": "Email not found. Please login again.",
      "financials.alert.success": "Financial data saved successfully!",
      "financials.alert.fail": "Failed to save financial data",
      "financials.alert.error": "Error saving financial data",

      // IR Score
      "irScore.title": "IR Score",
      "irScore.myApplications": "My Applications",
      "irScore.yourScore": "Your IR Score",
      "irScore.totalScore": "Total Score",
      "irScore.weightedScore": "Weighted Score / 100",
      "irScore.breakdown": "Breakdown by Category",
      "irScore.col.category": "Category",
      "irScore.col.score": "Score",
      "irScore.col.weight": "Weight",
      "irScore.col.weightedScore": "Weighted Score",
      "irScore.cat.companyProfile": "Company Profile",
      "irScore.cat.capitalReadiness": "Capital Readiness",
      "irScore.cat.growthArchitecture": "Growth Architecture",
      "irScore.cat.growthFinancial": "Growth + Financial IQ",
      "irScore.cat.governance": "Governance & Structure",
      "irScore.loading": "Loading...",
      "irScore.alert.login": "Please login to view IR Score",
      "irScore.alert.emailNotFound": "Email not found. Please login again.",
      "irScore.alert.error": "Error fetching user data",

      // IR Score Diagnostic Report Page
      "irScore.report.title": "Founder Diagnostic Report",
      "irScore.report.subtitle": "A personalized assessment of your capital readiness and next-step priorities.",
      "irScore.report.diagnosisSummary": "Diagnosis Summary",
      "irScore.report.loadingReport": "Loading your report...",
      "irScore.report.downloading": "Downloading...",
      "irScore.report.downloadReport": "Download Report (PDF)",
      "irScore.report.completeFirst": "Please complete the assessment first to download your report.",
      "irScore.report.noAssessment": "You haven't completed your Investor Readiness assessment yet. Complete the assessment to see your detailed diagnostic report.",
      "irScore.report.takeAssessment": "Take Assessment",
      "irScore.report.paragraph1": "You are at the starting line of your fundraising journey. This means you currently lack the core building blocks that investors expect before they will consider providing capital. At this stage, your company may still be operating on vision and early hustle rather than structured materials, validated numbers, or investor-ready proof points.",
      "irScore.report.paragraph2": "This is not a weakness — it is simply a signal that your focus must be on foundations, not fundraising. Approaching investors too soon can actually harm future chances, as first impressions carry weight. The fact that you've completed this diagnostic shows that you are self-aware and ready to begin laying down the path to investor credibility.",
      "irScore.report.paragraph3": "You are what we call a {{title}}: ambitious, driven, and at the very beginning of the readiness curve. Your goal now is to transform that dream into an investable story.",
      "irScore.report.col.capital": "Capital",
      "irScore.report.col.mvp": "MVP",
      "irScore.report.col.marketGrowth": "Market/Growth",
      "irScore.report.col.governance": "Governance",
      "irScore.report.row.dreamer": "Dreamer",
      "irScore.report.row.explorer": "Explorer",
      "irScore.report.row.builder": "Builder",
      "irScore.report.row.shaper": "Shaper",
      "irScore.report.row.challenger": "Challenger",
      "irScore.report.row.trailblazer": "Trailblazer",
      "irScore.report.row.visionary": "Visionary",
      "irScore.report.desc.1.R1": "You're laying the earliest bricks of your investor story.",
      "irScore.report.desc.1.R2": "Learning the ropes of fundraising and value creation.",
      "irScore.report.desc.1.R3": "Beginning to assemble your capital and valuation story.",
      "irScore.report.desc.1.R4": "You've got a draft story; it's time to sharpen it.",
      "irScore.report.desc.1.R5": "Your materials are strong, just need polishing.",
      "irScore.report.desc.1.R6": "You're compelling to investors; next is momentum.",
      "irScore.report.desc.1.R7": "An inspiring investment story; fundable and future-ready.",
      "irScore.report.desc.2.R1": "You're shaping your first vision into reality.",
      "irScore.report.desc.2.R2": "Testing bold ideas through experiments.",
      "irScore.report.desc.2.R3": "Turning sketches into prototypes.",
      "irScore.report.desc.2.R4": "Your product is forming; refinement is next.",
      "irScore.report.desc.2.R5": "Your build is near-ready to impress users.",
      "irScore.report.desc.2.R6": "You've got a validated product; ready to scale.",
      "irScore.report.desc.2.R7": "A standout product with wow-factor.",
      "irScore.report.desc.3.R1": "You're imagining your first customers.",
      "irScore.report.desc.3.R2": "Searching for first traction opportunities.",
      "irScore.report.desc.3.R3": "Piecing together funnels and early sales.",
      "irScore.report.desc.3.R4": "Customers are coming in; now focus on retention.",
      "irScore.report.desc.3.R5": "Revenue playbook in progress, scaling soon.",
      "irScore.report.desc.3.R6": "Revenue traction is solid; expansion awaits.",
      "irScore.report.desc.3.R7": "A growth engine admired by peers and investors.",
      "irScore.report.desc.4.R1": "You're starting to sketch the rules of your game.",
      "irScore.report.desc.4.R2": "Curious about setting up the right foundation.",
      "irScore.report.desc.4.R3": "First steps in structuring equity and docs.",
      "irScore.report.desc.4.R4": "Early frameworks exist; make them stronger.",
      "irScore.report.desc.4.R5": "Governance is working, needs fine-tuning.",
      "irScore.report.desc.4.R6": "Systems are credible; primed for scale.",
      "irScore.report.desc.4.R7": "A model company with exemplary structure.",
      "irScore.report.risk.criticalRisk": "Critical Risk",
      "irScore.report.risk.veryHighRisk": "Very High Risk",
      "irScore.report.risk.highRisk": "High Risk",
      "irScore.report.risk.mediumRisk": "Medium Risk",
      "irScore.report.risk.semiReady": "Semi-Ready",
      "irScore.report.risk.investorReady": "Investor-Ready",
      "irScore.report.risk.exceptional": "Exceptional",


      // KPIs
      "kpis.title": "KPIs",
      "kpis.myApplications": "My Applications",
      "kpis.yourKpis": "Your KPIs",
      "kpis.infoText": "Track your key performance indicators to monitor growth and identify areas for improvement.",

      // Unit Economics
      "kpis.unitEconomics.title": "Unit Economics",
      "kpis.unitEconomics.desc": "Understand the value and cost per customer.",
      "kpis.ltvLabel": "LTV:",
      "kpis.ltvDesc": "The expected total gross profit you earn from a typical customer over the full length of their relationship.\nLTV = (ARPU × Gross Margin) ÷ Monthly Churn",
      "kpis.ltvPlaceholder": "LTV",
      "kpis.cacLabel": "CAC:",
      "kpis.cacDesc": "The fully loaded sales & marketing cost to acquire one new paying customer.\nCAC = (Sales & Marketing Spend ÷ New Customers Acquired)",
      "kpis.cacPlaceholder": "CAC",
      "kpis.arpuLabel": "ARPU:",
      "kpis.arpuDesc": "ARPU (Average Revenue Per User) is the average revenue per active user in a given period—total revenue ÷ number of active users.\nARPU = (Total revenue in the period ÷ number of active users in the period)",
      "kpis.arpuPlaceholder": "ARPU",
      "kpis.paybackLabel": "Payback Period(months):",
      "kpis.paybackDesc": "How many months does it take for gross profit from a new customer to recover CAC?\nPayback Period (months)=CAC ÷ (ARPU × Gross Margin %)",
      "kpis.paybackPlaceholder": "Payback Period (months)",

      // Retention & Churn
      "kpis.retention.title": "Retention & Churn",
      "kpis.retention.desc": "Track customer loyalty and recurring revenue stability.",
      "kpis.nrrLabel": "Net Revenue Retention (NRR):",
      "kpis.nrrDesc": "Measures net growth from the existing customer base after upgrades, downgrades, and churn.\nNRR=(Starting MRR + Expansion – Contraction – Churn) ÷ Starting MRR",
      "kpis.nrrPlaceholder": "Net Revenue Retention (NRR)",
      "kpis.mrrLabel": "MRR (Monthly Recurring Revenue):",
      "kpis.mrrDesc": "Is the total predictable subscription revenue from active subscriptions in a given month (excluding one-time charges).\nmrr:MRR = Σ (Plan Price × # Active Subscriptions)",
      "kpis.mrrPlaceholder": "MRR (Monthly Recurring Revenue)",
      "kpis.churnRateLabel": "MRR Churn % (monthly):",
      "kpis.churnRateDesc": "Percent of recurring revenue lost in a month due to cancellations or downgrades.\nChurned MRR ÷ Starting MRR",
      "kpis.churnRatePlaceholder": "MRR Churn % (monthly)",

      // Growth & Efficiency
      "kpis.growth.title": "Growth & Efficiency",
      "kpis.growth.desc": "Evaluate how effectively you scale your revenue.",
      "kpis.magicNumberLabel": "Magic Number:",
      "kpis.magicNumberDesc": "Indicates how efficiently last quarter's sales & marketing spend converts into new ARR.\n(ARR_t - ARR_(t-1)) * 4) ÷ S&M_(t-1)",
      "kpis.magicNumberPlaceholder": "Magic Number",
      "kpis.burnMultipleLabel": "Burn Multiple:",
      "kpis.burnMultipleDesc": "Shows cash efficiency by telling how many dollars are burned to add one dollar of new ARR.\nBurn Multiple = Net Burn ÷ Net New ARR",
      "kpis.burnMultiplePlaceholder": "Burn Multiple",

      // Liquidity & Runway
      "kpis.liquidity.title": "Liquidity & Runway",
      "kpis.liquidity.desc": "Measure cash position and sustainability.",
      "kpis.netBurnLabel": "Net Burn (monthly):",
      "kpis.netBurnDesc": "The company's net cash loss per month at current operating pace.\nNet Burn=Cash Outflows - Cash Inflows",
      "kpis.netBurnPlaceholder": "Net Burn (monthly)",
      "kpis.runwayLabel": "Runway (months):",
      "kpis.runwayDesc": "The number of months the company can operate before cash runs out at current burn.\nRunway=Current Cash ÷ Net Burn",
      "kpis.runwayPlaceholder": "Runway (months)",

      "kpis.saveButton": "Save KPI Data",
      "kpis.saving": "Saving...",
      "kpis.alert.login": "Please login to save KPIs",
      "kpis.alert.emailNotFound": "Email not found. Please login again.",
      "kpis.alert.validation": "Please fill in all KPI fields before saving.",
      "kpis.alert.success": "KPIs saved successfully!",
      "kpis.alert.fail": "Failed to save KPIs",
      "kpis.alert.error": "Error saving KPIs",





      // Data Room
      "dataRoom.title": "Data Room",
      "dataRoom.myApplications": "My Applications",
      "dataRoom.tab.companyDocuments": "Company Documents",
      "dataRoom.loading": "Loading your data room...",
      "dataRoom.noFiles": "No files uploaded yet",
      "dataRoom.uploading": "Uploading...",
      "dataRoom.uploadFile": "Upload File",
      "dataRoom.categories.pitchMaterials": "Pitch Materials",
      "dataRoom.categories.pitchMaterialsDesc": "Deck (PDF), One-pager",
      "dataRoom.categories.financials": "Financials",
      "dataRoom.categories.financialsDesc": "P&L, Cash Flow, Forecast",
      "dataRoom.categories.legal": "Legal",
      "dataRoom.categories.legalDesc": "Incorporation, Cap Table, Shareholder Resolutions",
      "dataRoom.categories.product": "Product",
      "dataRoom.categories.productDesc": "Demo video, Screenshots",
      "dataRoom.alert.loadError": "Failed to load files. Please try again.",
      "dataRoom.alert.login": "Please sign in to upload files",
      "dataRoom.alert.uploadSuccess": "{{count}} file(s) uploaded successfully!",
      "dataRoom.alert.uploadFailed": "Failed to upload files.",
      "dataRoom.alert.errorUnauthorized": "Permission denied. Please ensure you are logged in.",
      "dataRoom.alert.errorUnauthenticated": "Not authenticated. Please log in and try again.",
      "dataRoom.alert.errorTimeout": "Upload timeout. Please check your internet connection.",
      "dataRoom.alert.errorNetwork": "Network error. Please contact support.",
      "dataRoom.alert.errorUnknown": "Unknown error. Please try again.",
      "dataRoom.alert.loginDelete": "Please login to delete files",
      "dataRoom.alert.confirmDelete": "Are you sure you want to delete \"{{fileName}}\"?",
      "dataRoom.alert.deleteSuccess": "File deleted successfully!",
      "dataRoom.alert.deleteFailed": "Failed to delete file. Please try again.",
      "goal.alertForm1First": "Please complete Form 1 first",
      "form1.q1": "What is your current runway?",
      "form1.q1opt1": "Less than 6 months",
      "form1.q1opt2": "6-12 months",
      "form1.q1opt3": "12-18 months",
      "form1.q1opt4": "18-24 months",
      "form1.q1opt5": "Over 24 months",
      "form1.q2": "How much capital are you planning to raise?",
      "form1.q2placeholder": "Enter amount (e.g., $500,000)",
      "form1.q3": "What is your annual operating cost (burn rate)?",
      "form1.q3placeholder": "Enter amount (e.g., $300,000)",
      "form1.q4": "What is your current company valuation?",
      "form1.q4placeholder": "Enter amount (e.g., $2,000,000)",
      "form1.q5": "When do you expect to generate revenue?",
      "form1.q5opt1": "Already generating",
      "form1.q5opt2": "Within 3 months",
      "form1.q5opt3": "Within 6 months",
      "form1.q5opt4": "Within 12 months",
      "form1.q5opt5": "12+ months",
      "form1.q5opt6": "Uncertain",
      "form1.q6": "What is your next major milestone?",
      "form1.q6opt1": "Product launch",
      "form1.q6opt2": "Strategic partnership",
      "form1.q6opt3": "Cost reduction",
      "form1.q6opt4": "New funding round",
      "form1.q6opt5": "Other",
      "form1.q7": "Do you have any existing debt?",
      "form1.q7opt1": "Yes",
      "form1.q7opt2": "No",
      "form1.q8": "What type of debt do you have? (Select all that apply)",
      "form1.q8opt1": "Convertible notes",
      "form1.q8opt2": "Venture debt",
      "form1.q8opt3": "Bank loans",
      "form1.q8opt4": "Overdue payables",
      "form1.q8opt5": "Other liabilities",

      // Form 2 - Investor Readiness Part 2
      "form2.q1": "Do you have any overdue payables?",
      "form2.q1opt1": "yes - minor amounts",
      "form2.q1opt2": "under control",
      "form2.q1opt3": "yes - significant and impacting operations",
      "form2.q1opt4": "No",
      "form2.q2": "How stable is your startup's revenue?",
      "form2.q2opt1": "None",
      "form2.q2opt2": "Irregular",
      "form2.q2opt3": "Somewhat Stable",
      "form2.q2opt4": "Mostly Stable",
      "form2.q2opt5": "Recurring",
      "form2.q3": "How would you react if your startup faced a major unexpected setback (e.g., key client loss, funding delay)?",
      "form2.q3opt1": "Pivot strategy",
      "form2.q3opt2": "Cut costs and extend runway",
      "form2.q3opt3": "Seek bridge funding",
      "form2.q3opt4": "Pause operations and restructure",
      "form2.q4": "How much experience do you have in founding or leading startups?",
      "form2.q4opt1": "I don't have experience",
      "form2.q4opt2": "1 to 5 years",
      "form2.q4opt3": "6 to 10 years",
      "form2.q4opt4": "More than 10 years",
      "form2.q5": "What relevant strengths does your founding team bring? (You can pick one or multiple choices)",
      "form2.q5opt1": "Successful prior startup exit",
      "form2.q5opt2": "Leadership at a fast-growing company",
      "form2.q5opt3": "Deep technical expertise",
      "form2.q5opt4": "Strong industry network",
      "form2.q5opt5": "Proven fundraising track record",
      "form2.q6": "How do you approach major strategic decisions for your startup?",
      "form2.q6opt1": "Data-driven analysis",
      "form2.q6opt2": "Consult advisors",
      "form2.q6opt3": "Follow instinct",
      "form2.q6opt4": "Delay decisions",
      "form2.q7": "How do you view taking big risks to achieve your startup's goals?",
      "form2.q7opt1": "Prefer minimizing risks",
      "form2.q7opt2": "Open to moderate risks",
      "form2.q7opt3": "Comfortable with big risks",
      "form2.q7opt4": "Delay decisions",
      "form2.complete": "Complete →",

      // Sidebar
      // "sidebar.dashboard": "Dashboard", // Removed duplicate
      // "sidebar.company": "Company",
      // "sidebar.profile": "Profile",
      // "sidebar.dataRoom": "Data Room",
      // "sidebar.financials": "Financials",
      // "sidebar.irScore": "IR Score",
      // "sidebar.kpis": "KPIs",
      // "sidebar.services": "Services",
      // "sidebar.referAndEarn": "Refer and Earn",
      // "sidebar.settings": "Settings",
      // "sidebar.signOut": "Sign Out",

      // Admin Sidebar
      "adminSidebar.dashboard": "Dashboard",
      "adminSidebar.raisingRequest": "Raising Request",
      "adminSidebar.partners": "Partners",
      "adminSidebar.adminProfile": "Admin Profile",
      "adminSidebar.settings": "Settings",
      "adminSidebar.sendInvitation": "Send Invitation",
      "adminSidebar.logout": "Logout",

      // Admin Page

      // Dashboard
      "dashboard.welcomeBack": "Welcome back, {{name}}! 👋",
      "dashboard.signedIn": "You're successfully signed in",
      "dashboard.completeCheck": "Complete your free Investor Readiness check",
      "dashboard.getScoreDesc": "Get your Investment Score, boost fundraising odds, and unlock tailored consulting.",
      "dashboard.complete": "Complete",
      "dashboard.capitalReadiness": "Capital Readiness",
      "dashboard.capitalReadinessDesc": "Fundraising and investor preparation: pitch decks, valuation, data room, and investor matching.",
      "dashboard.growthArchitecture": "Growth Architecture",
      "dashboard.growthArchitectureDesc": "Product build and validation are your main goals — focus on MVP completion, roadmap clarity, and traction.",
      "dashboard.growthFinancial": "Growth + Financial Intelligence",
      "dashboard.growthFinancialDesc": "Financial discipline drives your growth — focus on revenue, KPIs, and forecasting.",
      "dashboard.governance": "Governance & Structure",
      "dashboard.governanceDesc": "Governance and structure dominate — focus on leadership, compliance, and systems.",
      "dashboard.goalAssessment": "Goal Assessment",
      "dashboard.irScore": "IR Score",
      "dashboard.totalScore": "Total Score",
      "dashboard.profileTitle": "Profile Title",
      "dashboard.viewMore": "View more",
      "dashboard.completeNow": "Complete Now",
      "dashboard.majorGaps": "Major foundational gaps remain. Address pitch, metrics, and diligence prep to improve.",
      "dashboard.completeToSee": "Complete the IR Score assessment to see your rating",
      "dashboard.foundationDreamer": "You're laying the earliest bricks of your investor story. Keep momentum with guided sprints.",
      "dashboard.pricing": "Pricing",
      "dashboard.uploadDocuments": "Upload your documents",
      "dashboard.uploadDocumentsDesc": "Upload documents required for your mortgage application",
      "dashboard.speakConsultant": "Speak with a Conncct Consultant",
      "dashboard.speakConsultantDesc": "Unlock tailored support, advanced tools, and full platform access — all designed to accelerate your business growth.",
      "dashboard.upgrade": "Upgrade",
      "dashboard.readyToRaise": "Are you ready to raise capital?",
      "dashboard.readyToRaiseDesc": "Everything you need to secure investors — business plan, valuation, compliance, and data room setup.",
      "dashboard.startNow": "Start Now",
      "dashboard.freeConsultation": "Free Consultation",
      "dashboard.freeConsultationDesc": "Have a question? Ask our experts about business matters, your account, our products, and more.",
      "dashboard.bookNow": "Book Now",
      "dashboard.taxFilings": "Tax & Corporate Filings",
      "dashboard.taxFilingsDesc": "VAT registration & returns, Corporate Tax consultation & submission.",
      "dashboard.legalSupport": "Legal Support Tailored for Your Stage",
      "dashboard.legalSupportDesc": "Whether you're just starting or scaling your business, our legal team helps you stay protected and investor-ready.",
      "dashboard.documents": "Documents",
      "dashboard.consultations": "Consultations",
      "dashboard.scoreDetails": "Score Details",
      "dashboard.gapsBreakdown": "Gaps breakdown",
      "dashboard.planBreakdown": "Plan breakdown",
      "dashboard.viewLess": "View less",
      "dashboard.select": "Select",
      "dashboard.gaps.team": "Team & leadership: Key roles/advisors missing (major).",
      "dashboard.gaps.market": "Market clarity & fit: Problem/customer not validated (major).",
      "dashboard.gaps.model": "Business model & pricing: Unclear/untested (major).",
      "dashboard.gaps.traction": "Traction & proof: Limited users/revenue or pilots (major).",
      "dashboard.gaps.financial": "Financial plan & runway: No model / short runway (major).",
      "dashboard.gaps.pitch": "Pitch & materials: Story/deck not investor-grade (moderate).",
      "dashboard.gaps.compliance": "Compliance & DD: Legal/cap-table hygiene incomplete (moderate).",
      "dashboard.plan.team": "Add core talent + advisors (close critical skill gaps).",
      "dashboard.plan.market": "Validate market (15–20 interviews, 3–5 LOIs/pilots).",
      "dashboard.plan.model": "Define model (pricing, unit economics; test small cohort).",
      "dashboard.plan.mvp": "Build MVP & first traction (users/revenue; track simple KPIs).",
      "dashboard.plan.budget": "12–18-month budget & runway (control burn; set target raise).",
      "dashboard.plan.pitch": "Pitch v1 (10–12 slides) + FAQ (evidence-based story).",
      "dashboard.plan.dataroom": "Data-room starter (incorp, cap table, metrics, contracts).",
      "dashboard.foundation": "Foundation",
      "dashboard.capital": "Capital",
      "dashboard.mvp": "MVP",
      "dashboard.market/growth": "Market/Growth",
      "dashboard.marketGrowth": "Market/Growth",
      "dashboard.dreamer": "Dreamer",
      "dashboard.explorer": "Explorer",
      "dashboard.builder": "Builder",
      "dashboard.shaper": "Shaper",
      "dashboard.challenger": "Challenger",
      "dashboard.trailblazer": "Trailblazer",
      "dashboard.visionary": "Visionary",
      "dashboard.welcome": "Welcome back,",
      "dashboard.subtitle": "Here's what's happening with your applications today.",
      "dashboard.myApplications": "My Applications",
      "dashboard.createApp": "Create New Application",
      "dashboard.card.startups": "Startups",
      "dashboard.card.investors": "Investors",
      "dashboard.card.partners": "Partners",
      "dashboard.viewDashboard": "View Dashboard",
      "dashboard.recentActivity": "Recent Activity",
      "dashboard.noActivity": "No recent activity",

      "dashboard.risk.critical": "Critical Risk",
      "dashboard.risk.veryHigh": "Very High Risk",
      "dashboard.risk.high": "High Risk",
      "dashboard.risk.medium": "Medium Risk",
      "dashboard.risk.semiReady": "Semi-Ready",
      "dashboard.risk.investorReady": "Investor-Ready",
      "dashboard.risk.exceptional": "Exceptional",

      "dashboard.loading": "Loading your dashboard...",
      "dashboard.userFallback": "User",
      "dashboard.notAssessed": "Not Assessed",

      // Company Profile
      "companyProfile.title": "Company Profile",
      "companyProfile.myApplications": "My Applications",
      "companyProfile.yourInfo": "Your Information",
      "companyProfile.companyNameLabel": "Company Name: [Legal] — \"As per Trade License\"",
      "companyProfile.brandNameLabel": "Brand/Operating Name (Optional)",
      "companyProfile.incorporationDateLabel": "Date of Incorporation",
      "companyProfile.registeredAddressLabel": "Registered Address",
      "companyProfile.websiteLabel": "Website",
      "companyProfile.oneLineDescLabel": "One-line Description (140 chars)",
      "companyProfile.longDescLabel": "Long Description (1-2 paragraphs)",
      "companyProfile.sectorLabel": "Sector / Sub-sector",
      "companyProfile.foundersLabel": "Founders & Key Team (Name, Role, LinkedIn)",
      "companyProfile.saveChanges": "Save Changes",
      "companyProfile.saving": "Saving...",
      "companyProfile.alert.mandatory": "Please fill in all mandatory fields",
      "companyProfile.alert.login": "Please login to save changes",
      "companyProfile.alert.success": "Company profile saved successfully",
      "companyProfile.alert.error": "Error saving company profile",
      "companyProfile.placeholder.companyName": "Enter legal company name",
      "companyProfile.placeholder.brandName": "Enter brand name",
      "companyProfile.placeholder.date": "Select date",
      "companyProfile.placeholder.address": "Enter registered address",
      "companyProfile.placeholder.website": "Enter website URL",
      "companyProfile.placeholder.oneLineDesc": "Brief description of your company",
      "companyProfile.placeholder.longDesc": "Detailed description of your company, products, and vision",
      "companyProfile.placeholder.sector": "e.g. Fintech, E-commerce",
      "companyProfile.placeholder.founders": "List founders and key team members",


      // User Settings Page
      "settings.breadcrumb.myApplications": "My Applications",
      "settings.breadcrumb.settings": "Settings",
      "settings.title": "Settings",
      "settings.password.title": "Password",
      "settings.password.subtitle": "Manage your account password and billing preferences.",
      "settings.password.oldLabel": "Old Password",
      "settings.password.oldPlaceholder": "Enter Your Password",
      "settings.password.newLabel": "New Password",
      "settings.password.newPlaceholder": "Enter Your New Password",
      "settings.password.cancel": "Cancel",
      "settings.password.continue": "Continue",
      "settings.password.continuing": "Continuing...",
      "settings.password.error.fillAll": "Please fill in all password fields",
      "settings.password.error.minChars": "New password must be at least 6 characters",
      "settings.password.success": "Password changed successfully! Logging out...",
      "settings.password.error.failed": "Failed to change password",
      "settings.password.error.generic": "Error changing password. Please try again.",
      "settings.deleteAccount.title": "Delete Account",
      "settings.deleteAccount.subtitle": "Permanently delete your account and all of your content. This action is not reversible.",
      "settings.deleteAccount.button": "Delete Account",
      "settings.payment.comingSoon": "Payment integration coming soon!",

      // Demo Schedule (Calendly) Page
      "demoSchedule.title": "Ready to Talk? Pick a Time Below.",
      "demoSchedule.subtitle": "Schedule a 15-minute call with our team to discuss how Conncct can help your business.",
      "demoSchedule.noTime": "Can't find a suitable time?",
      "demoSchedule.contactUs": "Contact us directly",
      "demoSchedule.footer": "By scheduling a call, you agree to our",
      "demoSchedule.termsOfService": "Terms of Service",
      "demoSchedule.and": "and",
      "demoSchedule.privacyPolicy": "Privacy Policy",


    }
  },
  ar: {
    translation: {
      // Navbar
      "nav.partnerWithUs": "كن شريكاً معنا",
      "nav.resources": "الموارد",
      "nav.about": "من نحن",
      "nav.careers": "الوظائف",
      "nav.pricing": "الأسعار",
      "nav.alreadyHaveAccount": "لديك حساب بالفعل؟",
      "nav.signIn": "تسجيل الدخول",
      "nav.arabic": "English",
      "nav.getDemo": "احصل على عرض توضيحي",
      "nav.createAccount": "إنشاء حساب مجاني",

      // Top Banner
      "topBanner.winUpTo": "اربح ما يصل إلى",
      "topBanner.winDesc": "5,000 دولار في خدمات استشارية لمدة عام!",
      "topBanner.applyNow": "قدم الآن",

      // Admin
      "admin.searchPlaceholder": "البحث عن طريق رقم الحساب، البريد الإلكتروني، الهاتف أو الاسم",
      "admin.clientsOverview": "نظرة عامة على العملاء",
      "admin.newClients": "عملاء جدد",
      "admin.last30Days": "آخر 30 يومًا",
      "admin.allClients": "جميع العملاء",
      "admin.totalRegistered": "إجمالي المسجلين",
      "admin.applyFilters": "تطبيق الفلاتر",
      "admin.quickFilters": "فلاتر سريعة",
      "admin.risk.critical": "مخاطر حرجة",
      "admin.risk.veryHigh": "مخاطر عالية جداً",
      "admin.risk.high": "مخاطر عالية",
      "admin.risk.medium": "مخاطر متوسطة",
      "admin.risk.semiReady": "شبه جاهز",
      "admin.risk.investorReady": "جاهز للمستثمر",
      "admin.risk.exceptional": "استثنائي",
      "admin.product.raising": "طلب تمويل",
      "admin.product.consulting": "استشارات",
      "admin.score.yes": "نعم",
      "admin.score.no": "لا",
      "admin.referralTable": "جدول الإحالات",
      "admin.table.name": "الاسم",
      "admin.table.email": "البريد الإلكتروني",
      "admin.table.irScore": "نتيجة IR",
      "admin.table.phone": "الهاتف",
      "admin.table.products": "المنتجات",
      "admin.table.joined": "انضم منذ",
      "admin.table.action": "الإجراء",
      "admin.table.view": "عرض",
      "admin.filter.allCountries": "جميع البلدان",
      "admin.filter.startDate": "تاريخ البدء",
      "admin.filter.endDate": "تاريخ الانتهاء",
      "admin.filter.allProducts": "جميع المنتجات",
      "admin.filter.allScores": "جميع الدرجات",
      "admin.filter.allResults": "جميع النتائج",
      "admin.filter.newClients": "عملاء جدد",
      "admin.filter.paidClients": "مدفوع",
      "admin.filter.nonPaidClients": "غير مدفوع",
      "admin.filter.completedKYC": "اكتمل KYC",
      "admin.filter.notCompletedKYC": "لم يكتمل KYC",

      // Admin User Detail
      "admin.userDetail.loading": "يتحميل بيانات المستخدم...",
      "admin.userDetail.notFound": "المستخدم غير موجود",
      "admin.userDetail.back": "العودة إلى الإدارة",
      "admin.userDetail.tabs.goal": "الهدف",
      "admin.userDetail.tabs.irScore": "نتيجة IR",
      "admin.userDetail.tabs.profile": "الملف الشخصي",
      "admin.userDetail.tabs.companyProfile": "ملف الشركة",
      "admin.userDetail.tabs.financials": "البيانات المالية",
      "admin.userDetail.tabs.kpis": "المؤشرات",
      "admin.userDetail.tabs.dataRoom": "غرفة البيانات",
      "admin.userDetail.tabs.raiseCapital": "طلب تمويل",
      "admin.userDetail.raiseCapitalSubmission": "تقديم طلب تمويل",
      "admin.userDetail.viewAnswers": "عرض الإجابات",
      "admin.userDetail.noGoalAssessment": "لم يتم تقديم تقييم الأهداف بعد.",
      "admin.userDetail.noRaiseCapital": "لم يتم العثور على طلب تمويل لهذا المستخدم.",
      "admin.userDetail.submitted": "تم التقديم",

      // Assessment Questions & Options
      "assessment.goal.focus": "ما هو تركيزك الأول الآن؟",
      "assessment.goal.focus.raise": "جمع الأموال",
      "assessment.goal.focus.product": "بناء المنتج",
      "assessment.goal.focus.sales": "زيادة المبيعات",
      "assessment.goal.focus.ops": "إعداد العمليات",
      "assessment.goal.time": "أين يذهب معظم وقتك؟",
      "assessment.goal.time.investor": "عمل المستثمرين",
      "assessment.goal.time.product": "بناء المنتج",
      "assessment.goal.time.sales": "المبيعات والتسويق",
      "assessment.goal.time.ops": "العمليات والقانون",
      "assessment.goal.win": "كيف سيبدو الفوز بعد 6 أشهر بالنسبة لك؟",
      "assessment.goal.win.funded": "جولة ممولة",
      "assessment.goal.win.live": "منتج حي",
      "assessment.goal.win.revenue": "نمو الإيرادات",
      "assessment.goal.win.ops": "عمليات منظمة",
      "assessment.goal.holding": "ما الذي يعيقك أكثر اليوم؟",
      "assessment.goal.holding.knowhow": "خبرة المستثمرين",
      "assessment.goal.holding.test": "بناء الاختبار",
      "assessment.goal.holding.customer": "نمو العملاء",
      "assessment.goal.holding.legal": "الإعداد القانوني",
      "assessment.goal.step": "ما هي الخطوة التي يمكنك اتخاذها هذا الأسبوع؟",
      "assessment.goal.step.now": "طلب تمويل الآن",
      "assessment.goal.step.features": "بناء ميزات",
      "assessment.goal.step.marketing": "إطلاق التسويق",
      "assessment.goal.step.coo": "توظيف مدير عمليات",

      "assessment.raise.amount": "ما المبلغ الذي تتطلع لجمعه؟",
      "assessment.raise.round": "ما هي الجولة التي تجمع لها التمويل؟",
      "assessment.raise.capitalType": "ما نوع رأس المال الذي تجمعه؟",
      "assessment.raise.valuation": "هل لديك تقييم مستهدف أو سقف؟ (اختياري)",
      "assessment.raise.companyInfo": "أخبرنا عن شركتك",
      "assessment.raise.website": "الموقع الإلكتروني",

      // IR Score Data Room
      "assessment.ir.financialHeading": "البيانات المالية والتشغيلية",
      "assessment.ir.strategicHeading": "التقييم الاستراتيجي وفريق العمل",
      "assessment.ir.runway": "ما هو مدرجك المالي الحالي (رأس المال الأولي مقابل معدل الحرق الشهري)؟",
      "assessment.ir.capital": "ما مقدار رأس المال الذي تخطط لجمعه في جولتك التمويلية الحالية أو القادمة؟",
      "assessment.ir.burnRate": "ما هي تكلفة التشغيل السنوية/الشهرية (معدل الحرق)؟",
      "assessment.ir.valuation": "ما هو أحدث تقييم لشركتك (بعد التمويل أو قبله)؟",
      "assessment.ir.timeline": "ما هو الجدول الزمني المتوقع للإيرادات للوصول إلى نقطة التعادل؟",
      "assessment.ir.milestone": "ما هو الجدول الزمني المتوقع لتحقيق الإنجاز الرئيسي التالي الذي يدعم الوصول إلى نقطة التعادل النقدي؟",
      "assessment.ir.debt": "هل لديك أي ديون؟",
      "assessment.ir.hasDebt": "لديه ديون",
      "assessment.ir.debtTypes": "أنواع الديون",
      "assessment.ir.overdue": "ما هو إجمالي الذمم الدائنة القائمة أو المتأخرة لشركتك؟",
      "assessment.ir.stability": "ما مدى استقرار وإمكانية التنبؤ بإيراداتك؟",
      "assessment.ir.teamStrengths": "ما الذي يصف نقاط القوة الأساسية لفريقك بشكل أفضل؟",

      // Company Profile
      "company.name": "اسم الشركة [قانوني] - *كما في الرخصة التجارية*",
      "company.brand": "اسم العلامة التجارية/الاسم التجاري (اختياري)",
      "company.incDate": "تاريخ التأسيس",
      "company.address": "العنوان المسجل",
      "company.website": "الموقع الإلكتروني",
      "company.desc": "وصف من سطر واحد (140 حرفاً)",
      "company.longDesc": "وصف مطول (1-2 فقرة)",
      "company.sector": "القطاع/الصناعة",
      "company.team": "المؤسسون والفريق الرئيسي (الأسماء، الدور، لينكد إن)",
      "company.noData": "لم يتم تقديم بيانات ملف الشركة بعد.",

      // Financials
      "financials.revenue": "الإيرادات (ITM - آخر 12 شهرًا):",
      "financials.ebitda": "الأرباح قبل الفوائد والضرائب والإهلاك والاستهلاك (EBITDA):",
      "financials.cash": "النقد المتاح:",
      "financials.equity": "إجمالي الملكية:",
      "financials.mrr": "الإيرادات الشهرية المتكررة (MRR):",
      "financials.noData": "لم يتم تقديم بيانات مالية بعد.",

      // KPIs
      "kpis.unitEconomics": "اقتصاديات الوحدة",
      "kpis.retention": "الاحتفاظ والتحول",
      "kpis.growth": "النمو والكفاءة",
      "kpis.liquidity": "السيولة والمدرج",
      "kpis.noData": "لم يتم تقديم بيانات مؤشرات الأداء.",

      // Data Room
      "dataRoom.category.Pitch Materials": "مواد العرض التقديمي",
      "dataRoom.category.Financials": "البيانات المالية",
      "dataRoom.category.Legal": "قانوني",
      "dataRoom.category.Product": "المنتج",
      "dataRoom.file": "ملف",
      "dataRoom.files": "ملفات",

      // Hero Section
      "hero.title": "جهّز شركتك الناشئة للنمو والاستثمار",
      "hero.subtitle": "تقييم + أدوات + استشارات لمساعدة شركتك الناشئة على الانتقال إلى المرحلة التالية.",
      "hero.createAccount": "إنشاء حساب مجاني",
      "hero.getDemo": "احصل على عرض توضيحي",
      "hero.quote": "كونكت جعلت جمع التمويل بسيطاً.",
      "hero.saraRamirez": "سارة راميريز",
      "hero.founder": "المؤسسة، دبي",

      // Featured Section
      "featured.title": "مميز في",

      // All In One Platform
      "platform.badge": "منصة شاملة",
      "platform.title": "كل ما تحتاجه لجمع رأس المال وتوسيع نطاق عملك، في مكان واحد.",

      // Assessment Section
      "assessment.badge": "التقييم",
      "assessment.title": "درجة الجاهزية للمستثمرين",
      "assessment.description": "احصل على تقييم شخصي لجاهزية شركتك الناشئة للتواصل مع المستثمرين.",
      "assessment.cta": "ابدأ التقييم",

      // Tools Section
      "tools.badge": "الأدوات",
      "tools.title": "غرفة البيانات والأدوات",
      "tools.description": "جميع المستندات والقوالب الأساسية في مكان واحد. قم ببناء عرض تقديمي، نموذج مالي، والمزيد.",
      "tools.cta": "استكشف الأدوات",

      // Consulting Section
      "consulting.badge": "الاستشارات",
      "consulting.title": "استشارات متخصصة",
      "consulting.description": "تواصل مع مستشارين يمكنهم مساعدتك في تحسين الاستراتيجية والماليات والعروض التقديمية.",
      "consulting.cta": "احجز استشارة",

      // Free Tools Section
      "freeTools.title": "أدوات مجانية للبدء",
      "freeTools.subtitle": "احصل على مجموعتنا من الموارد المجانية لبدء رحلة جمع التمويل",
      "freeTools.card1.title": "قالب العرض التقديمي للشركات الناشئة",
      "freeTools.card1.description": "قالب احترافي لعرض رؤيتك",
      "freeTools.card1.cta": "تحميل مجاني",
      "freeTools.card2.title": "قالب النموذج المالي",
      "freeTools.card2.description": "قالب إكسل للتوقعات المالية",
      "freeTools.card2.cta": "تحميل مجاني",
      "freeTools.card3.title": "حاسبة جدول رأس المال",
      "freeTools.card3.description": "تتبع توزيع الأسهم بسهولة",
      "freeTools.card3.cta": "استخدم الأداة",

      // How It Works
      "howItWorks.title": "كيف يعمل",
      "howItWorks.subtitle": "ثلاث خطوات بسيطة لتصبح جاهزاً للمستثمرين",
      "howItWorks.step1.number": "01",
      "howItWorks.step1.title": "إجراء التقييم",
      "howItWorks.step1.description": "أكمل تقييم الجاهزية الشامل للشركات الناشئة لتحديد نقاط القوة والفجوات لديك.",
      "howItWorks.step2.number": "02",
      "howItWorks.step2.title": "الوصول إلى الأدوات",
      "howItWorks.step2.description": "استخدم قوالبنا وأدواتنا لبناء عرضك التقديمي ونموذجك المالي وغرفة البيانات.",
      "howItWorks.step3.number": "03",
      "howItWorks.step3.title": "احصل على مساعدة خبراء",
      "howItWorks.step3.description": "تواصل مع المستشارين الذين يمكنهم تحسين استراتيجيتك وإعدادك لاجتماعات المستثمرين.",

      // Testimonials
      "testimonials.title": "موثوق به من قبل المؤسسين في جميع أنحاء المنطقة",
      "testimonials.subtitle": "شاهد كيف ساعدت كونكت الشركات الناشئة على جمع رأس المال والنمو",
      "testimonials.test1.quote": "كل شيء كان في مكان واحد - من القوالب القانونية إلى التواصل مع المستثمرين. وفرت علينا كونكت أشهر من العمل.",
      "testimonials.test1.name": "نادية المنصوري",
      "testimonials.test1.title": "Evoleft - أكملت وثائق ما قبل التمويل في 10 أيام",
      "testimonials.test2.quote": "منذ اليوم الأول، جعلتنا كونكت نشعر بالاستعداد للعرض. أعطت درجة الجاهزية المستثمرين ثقة حقيقية.",
      "testimonials.test2.name": "لينا فاروق",
      "testimonials.test2.title": "BrightPay - توسعت الآن في الإمارات والسعودية",
      "testimonials.test3.quote": "شعرنا أن مستشاري كونكت جزء من فريقنا. كانت رؤاهم حول مؤشرات الأداء وجمع التمويل غيرت قواعد اللعبة.",
      "testimonials.test3.name": "رامي خوري",
      "testimonials.test3.title": "Skillvise - أغلقت جولة جسرية بقيمة 850 ألف دولار بعد 3 أشهر من الاستشارات",
      "testimonials.test4.quote": "جعلت كونكت جمع التمويل بسيطاً - في غضون أسابيع، كانت غرفة البيانات وعرضنا التقديمي جاهزين للمستثمرين.",
      "testimonials.test4.name": "سارة حداد",
      "testimonials.test4.title": "Elevate - جمعت 2 مليون دولار جولة تمويل أولي في 45 يوماً",
      "testimonials.test5.quote": "حسّن فريق الاستشارات نموذجنا المالي والتقييم. فهمنا أخيراً ما يبحث عنه المستثمرون.",
      "testimonials.test5.name": "عمر صالح",
      "testimonials.test5.title": "Foodbridge AI - توقع التقييم بنسبة 15% وخفض الأخطاء الاستراتيجية",

      // FAQ Section
      "faq.title": "الأسئلة الشائعة",
      "faq.subtitle": "كل ما تحتاج لمعرفته عن كونكت",

      // Footer
      "footer.description": "المنصة الشاملة للشركات الناشئة لتصبح جاهزة للمستثمرين وجمع رأس المال.",
      "footer.company": "الشركة",
      "footer.aboutUs": "من نحن",
      "footer.careers": "الوظائف",
      "footer.contact": "اتصل بنا",
      "footer.resources": "الموارد",
      "footer.blog": "المدونة",
      "footer.legal": "قانوني",
      "footer.privacy": "سياسة الخصوصية",
      "footer.support": "الدعم",
      "footer.terms": "شروط الخدمة",
      "footer.allRightsReserved": "كونكت @ 2025 جميع الحقوق محفوظة.",

      // Product Suite Section
      "productSuite.badge": "مجموعة المنتجات",
      "productSuite.title": "المنصة المالية الشاملة للمؤسسين مثلك.",
      "productSuite.card1.title": "اعرف مدى جاهزيتك",
      "productSuite.card1.description": "من السهل معرفة مدى استعدادك لجمع رأس المال. تتبع درجة جاهزيتك للمستثمرين على الفور.",
      "productSuite.card1.cta": "تتبع درجتك",
      "productSuite.card2.title": "غرفة البيانات",
      "productSuite.card2.description": "احتفظ بجميع مستنداتك في مكان واحد — آمنة ومنظمة وجاهزة للمستثمرين.",
      "productSuite.card2.cta": "الوصول إلى مستنداتك",
      "productSuite.card3.title": "لوحة مؤشرات الأداء",
      "productSuite.card3.description": "احصل على جميع المقاييس الرئيسية التي يبحث عنها رأس المال الاستثماري والمستثمرون الملائكة.",
      "productSuite.card3.cta": "عرض مؤشرات الأداء الخاصة بك",
      "productSuite.expertCard.badge": "إرشاد الخبراء",
      "productSuite.expertCard.title": "احصل على المشورة من خبراء متمرسين — المحاسبة ومديري الشؤون المالية والمستشارين القانونيين عند الطلب.",
      "productSuite.expertCard.cta": "تحدث إلى خبير",

      // Why Conncct Section
      "whyConncct.badge": "الموارد",
      "whyConncct.title": "لماذا كونكت؟",
      "whyConncct.subtitle": "نحن لا نربط رواد الأعمال بالمستشارين فحسب — بل نساعدك على جمع التمويل بذكاء، والتوسع بشكل أسرع، والبقاء جاهزاً للمستثمرين.",
      "whyConncct.subtitle2": "انظر كيف تتم مقارنة العمل مع مستشار كونكت بالقيام به بمفردك",
      "whyConncct.entrepreneursCard.title": "لقد بنينا شركات ناشئة، وعرضنا على المستثمرين، وقمنا بتوسيع نطاق المنتجات بأنفسنا.",
      "whyConncct.entrepreneursCard.cta": "رواد الأعمال",
      "whyConncct.consultantsCard.title": "نحن نقدم البنية والاستراتيجية والجاهزية للمستثمرين في رحلتك.",
      "whyConncct.consultantsCard.cta": "المستشارون",

      // Built For Founders Section
      "builtForFounders.badge": "أكثر من 10,000 مؤسس",
      "builtForFounders.title": "مصمم للمؤسسين. محبوب من قبل المؤسسين.",

      // FAQ Page
      "faq.heading": "الأسئلة الشائعة",
      "faq.description": "الأسئلة الشائعة مرتبة حسب الشعبية. تذكر أنه إذا لم يلتزم الزائر بالدعوة إلى اتخاذ إجراء، فقد لا تزال لديه أسئلة (شكوك) يمكن الإجابة عليها.",
      "faq.q1.question": "ما هي كونكت؟",
      "faq.q1.answer": "كونكت هي منصة شاملة تساعد الشركات الناشئة والشركات الصغيرة والمتوسطة على الاستعداد لجمع التمويل، وإدارة غرف البيانات الخاصة بها، والوصول إلى استشارات الخبراء في مجالات التمويل والقانون والاستراتيجية — كل ذلك في مكان واحد.",
      "faq.q2.question": "هل أحتاج إلى أن أكون شركة راسخة لاستخدام كونكت؟",
      "faq.q2.answer": "لا، ليس بالضرورة. تعمل كونكت مع كل من الشركات الناشئة في مراحلها المبكرة والشركات الصغيرة والمتوسطة النامية. سواء كنت تجمع جولتك الأولى من التمويل أو تتوسع للسلسلة A، يمكن لمستشارينا مساعدتك على أن تكون جاهزاً للمستثمرين.",
      "faq.q3.question": "لماذا يجب أن أستخدم كونكت لجمع رأس المال؟",
      "faq.q3.answer": "تجمع كونكت كل ما يريده المستثمرون تحت سقف واحد — عرضك التقديمي، التقييم، مؤشرات الأداء الرئيسية، والماليات — مع ربطك بالمستثمرين والمستشارين الموثوقين.",
      "faq.q4.question": "ما المعلومات التي تحتاجها مني للبدء؟",
      "faq.q4.answer": "نحتاج فقط اسم شركتك، مرحلة العمل، وأهداف التمويل. بمجرد التسجيل، سيرشدك نموذج الإعداد الخاص بنا لإنشاء ملف الاستعداد وإعداد غرفة البيانات الخاصة بك.",
      "faq.q5.question": "ما هي كونكت للاستشارات؟",
      "faq.q5.answer": "تمنحك كونكت للاستشارات الوصول إلى مديري شؤون مالية ومستشارين قانونيين واستراتيجيين ذوي خبرة يصممون حلولاً تناسب أهداف نموك — من تحسين نموذج عملك إلى تحسين جاهزيتك للمستثمرين.",
      "faq.q6.question": "هل يمكن لكونكت مساعدتي في خطة العمل والتقييم؟",
      "faq.q6.answer": "بالتأكيد. يساعدك فريقنا في بناء خطة عمل مفصلة وتوقعات مالية ونموذج تقييم يتوافق مع توقعات المستثمرين في مجالك.",
      "faq.q7.question": "لمن صُممت كونكت؟",
      "faq.q7.answer": "صُممت كونكت للمؤسسين ورواد الأعمال وأصحاب الأعمال الصغيرة الذين يرغبون في جمع رأس المال بكفاءة والبقاء ملتزمين والتوسع استراتيجياً.",
      "faq.q8.question": "هل بياناتي آمنة على كونكت؟",
      "faq.q8.answer": "نعم. جميع بيانات عملك والمستثمرين مشفرة ومخزنة في غرفة البيانات الآمنة الخاصة بك، ولا يمكن الوصول إليها إلا من قبلك والشركاء المعتمدين.",

      // Login Page
      "login.welcomeTo": "مرحباً بك في",
      "login.portalName": "بوابة كونكت",
      "login.welcomeSubtitle": "الوصول إلى أدوات شركتك الناشئة والتشخيصات والتقارير في مكان واحد.",
      "login.signInTitle": "تسجيل الدخول إلى حسابك",
      "login.signInSubtitle": "أدخل بياناتك للوصول إلى لوحة التحكم.",
      "login.emailPlaceholder": "أدخل عنوان بريدك الإلكتروني",
      "login.passwordPlaceholder": "أدخل كلمة المرور",
      "login.title": "مرحباً بعودتك",
      "login.subtitle": "قم بتسجيل الدخول إلى حسابك للمتابعة",
      "login.email": "البريد الإلكتروني",
      "login.password": "كلمة المرور",
      "login.rememberMe": "تذكرني",
      "login.forgotPassword": "نسيت كلمة المرور؟",
      "login.signIn": "تسجيل الدخول",
      "login.noAccount": "ليس لديك حساب؟",
      "login.signUp": "إنشاء حساب",
      "login.or": "أو",
      "login.continueWithGoogle": "المتابعة مع جوجل",
      "login.signingIn": "جاري تسجيل الدخول...",
      "login.orContinueWith": "أو تابع مع",
      "login.dontHaveAccount": "ليس لديك حساب؟ أنشئ واحداً",

      // Onboarding Guard
      "onboardingGuard.redirecting": "جاري توجيهك إلى لوحة التحكم",

      // Signup Page
      "signup.title": "إنشاء حساب",
      "signup.subtitle": "انضم إلى كونكت لتصبح جاهزاً للمستثمرين",
      "signup.howCanHelp": "كيف يمكننا مساعدتك؟",
      "signup.investorReadinessTitle": "الجاهزية للمستثمرين ورأس المال",
      "signup.investorReadinessDesc": "للشركات الناشئة التي تستعد لجمع التمويل.",
      "signup.raiseCapitalTitle": "جمع رأس المال",
      "signup.raiseCapitalDesc": "للشركات الناشئة والشركات التي ترغب في جمع رأس المال",
      "signup.businessSetupTitle": "إعداد ونمو الأعمال",
      "signup.businessSetupDesc": "الوصول إلى تأسيس الشركات الناشئة والامتثال والدعم الاستشاري لإعداد وتوسيع نطاق عملك.",
      "signup.continue": "متابعة",
      "signup.yourJourney": "رحلة مؤسسك",
      "signup.startsHere": "تبدأ هنا",
      "signup.accessTools": "الوصول إلى الأدوات والتقييمات والاستشارات لمساعدتك على النمو وجمع رأس المال.",
      "signup.firstName": "الاسم الأول",
      "signup.lastName": "اسم العائلة",
      "signup.fullName": "الاسم الكامل",
      "signup.phoneNumber": "أدخل رقم الهاتف",
      "signup.email": "أدخل البريد الإلكتروني",
      "signup.password": "أدخل كلمة المرور",
      "signup.confirmPassword": "أدخل تأكيد كلمة المرور",
      "signup.createAccount": "إنشاء حسابي",
      "signup.haveAccount": "لديك حساب بالفعل؟",
      "signup.signIn": "تسجيل الدخول",
      "signup.or": "أو",
      "signup.continueWithGoogle": "المتابعة مع جوجل",
      "signup.back": "رجوع",
      "signup.fillAllFields": "يرجى ملء جميع الحقول",
      "signup.passwordMismatch": "كلمات المرور غير متطابقة",
      "signup.passwordTooShort": "يجب أن تتكون كلمة المرور من 6 أحرف على الأقل",
      "signup.invalidEmail": "يرجى إدخال بريد إلكتروني صالح",
      "signup.creatingAccount": "جارٍ إنشاء الحساب...",
      "signup.howCanHelpToday": "كيف يمكننا مساعدتك اليوم؟",
      "signup.selectService": "اختر الخدمة التي تناسب مرحلة عملك الحالية. سنرشدك خلال الخطوات التالية.",
      "signup.setupGrowthTitle": "الإعداد والنمو",
      "signup.setupGrowthSubtitle": "أنت تبني الشركة. نحن نبني الأساس.",
      "signup.setupGrowthFeature1": "التأسيس حيث يهم أكثر — الإمارات وأمريكا والمراكز العالمية",
      "signup.setupGrowthFeature2": "ضريبة الشركات وضريبة القيمة المضافة والإيداعات — مُدارة بالكامل",
      "signup.setupGrowthFeature3": "دفاتر حسابات نظيفة وتقارير جاهزة لأي مستثمر",
      "signup.setupGrowthCTA": "أسس شركتي",
      "signup.investorReadinessCardTitle": "الجاهزية للمستثمرين",
      "signup.investorReadinessSubtitle": "لديك شركة. الآن اجعلها شركة لا يستطيع أي مستثمر تجاهلها.",
      "signup.investorReadinessFeature1": "بيانات مالية تصمد أمام تدقيق رأس المال الجريء",
      "signup.investorReadinessFeature2": "عرض تقديمي مبني للحصول على اجتماعات ثانية",
      "signup.investorReadinessFeature3": "جميع الوثائق التي يريدها المستثمرون — جاهزة قبل أن يطلبوها",
      "signup.investorReadinessCTA": "أصبح قابلاً للتمويل",
      "signup.capitalRaisingTitle": "جمع رأس المال",
      "signup.capitalRaisingSubtitle": "أنت مستعد للجمع. نحن نجمع معك.",
      "signup.capitalRaisingFeature1": "غرفة بيانات يفتحها المستثمرون",
      "signup.capitalRaisingFeature2": "مطابقة مع رأس المال الجريء والملائكة ومكاتب العائلة المناسبة",
      "signup.capitalRaisingFeature3": "إلى جانبك — من أول اجتماع حتى نموذج الاستثمار",
      "signup.capitalRaisingCTA": "أطلق جمع رأس المال",

      // Capital flow stepper labels
      "capitalFlow.step1": "تقييم الهدف",
      "capitalFlow.step2": "جاهزية المستثمرين",
      "capitalFlow.step3": "تقييم رأس المال",

      // Capital Assessment Page
      "capitalAssessment.sectionLabel": "جمع رأس المال",
      "capitalAssessment.q1": "ما هي مرحلة شركتك الحالية؟",
      "capitalAssessment.q1opt1": "ما قبل الإيرادات / ما قبل المنتج",
      "capitalAssessment.q1opt1sub": "فكرة / نموذج أولي مبكر",
      "capitalAssessment.q1opt2": "نموذج أولي / زخم مبكر",
      "capitalAssessment.q1opt2sub": "بناء الزخم",
      "capitalAssessment.q1opt3": "تحقق إيرادات",
      "capitalAssessment.q1opt3sub": "عملاء يدفعون",
      "capitalAssessment.q1opt4": "مرحلة التوسع / النمو",
      "capitalAssessment.q1opt4sub": "نمو سريع",
      "capitalAssessment.q2": "كم تسعى إلى جمع؟",
      "capitalAssessment.q2opt1": "أقل من 500 ألف دولار",
      "capitalAssessment.q2opt2": "500 ألف – 2 مليون دولار",
      "capitalAssessment.q2opt3": "2 مليون – 10 مليون دولار",
      "capitalAssessment.q2opt4": "أكثر من 10 مليون دولار",
      "capitalAssessment.q3": "ما نوع التمويل الذي تبحث عنه؟",
      "capitalAssessment.q3opt1": "حقوق ملكية (رأس مال جريء / ملاك أعمال)",
      "capitalAssessment.q3opt2": "سند قابل للتحويل / SAFE",
      "capitalAssessment.q3opt3": "تمويل بالديون",
      "capitalAssessment.q3opt4": "لم أحدد بعد",
      "capitalAssessment.q4": "أين أنت في عملية جمع التمويل؟",
      "capitalAssessment.q4opt1": "في البداية تماماً",
      "capitalAssessment.q4opt2": "أبني مواد العرض",
      "capitalAssessment.q4opt3": "ألتقي بالمستثمرين بنشاط",
      "capitalAssessment.q4opt4": "في مرحلة الإغلاق",
      "capitalAssessment.q5": "ما هو أكبر تحدياتك في جمع التمويل؟",
      "capitalAssessment.q5opt1": "إيجاد المستثمرين المناسبين",
      "capitalAssessment.q5opt2": "التقييم وشروط الصفقة",
      "capitalAssessment.q5opt3": "وثيقة العرض وأسلوب السرد",
      "capitalAssessment.q5opt4": "العناية الواجبة والبيانات المالية",

      // Country Selection Page
      "country.selectCountry": "اختر بلدك",
      "country.chooseLocation": "اختر موقعك للبدء",
      "country.continue": "متابعة",
      "country.selectOne": "يرجى اختيار بلد",

      // About Page
      "about.hero": "دعنا نتواصل معك!",
      "about.visionBadge": "رؤيتنا",
      "about.vision": "عالم يكون فيه جمع التمويل منظماً وشفافاً ومعتمداً على البيانات — مما يمنح كل مؤسس طموح مساراً واضحاً وقابلاً للتكرار لرأس المال والنمو.",
      "about.missionBadge": "مهمتنا",
      "about.mission": "تزويد المؤسسين ورواد الأعمال، خاصة في الأسواق الناشئة، بالأدوات والرؤى والدعم الخبير اللازم ليصبحوا جاهزين للمستثمرين، وإغلاق جولات التمويل بشكل أسرع، وبناء شركات تتوسع بانضباط.",
      "about.whoBadge": "من نحن",
      "about.who1": "كونكت هي منصة جاهزية رأس المال وجمع التمويل المبنية على معايير عالمية ومبنية على رؤى إقليمية.",
      "about.who2": "نساعد المؤسسين على تجنب الأخطاء المكلفة، وبناء مواد على مستوى المستثمرين، والتنقل في عملية جمع التمويل بالهيكل والوضوح والسرعة.",
      "about.who3": "يقع عملنا عند تقاطع التمويل والتكنولوجيا والخبرة البشرية — تحويل كيفية تعاون الشركات الناشئة والمستثمرين والمستشارين لتنمية الأعمال المستدامة.",
      "about.cultureBadge": "ثقافتنا",
      "about.culture1": "نحن نؤمن بأن التغيير الهادف يحدث عندما تلتقي الاستراتيجية بالشغف.",
      "about.culture2": "الابتكار",
      "about.culture2Text": "نستخدم البيانات والذكاء الاصطناعي لتحدي نموذج الاستشارات التقليدي.",
      "about.culture3": "التعاون",
      "about.culture3Text": "نعمل عبر الصناعات والجغرافيا والأفكار.",
      "about.culture4": "النزاهة",
      "about.culture4Text": "نؤمن بالشفافية والمساءلة — داخل وخارج الشركة.",
      "about.culture5": "التأثير",
      "about.culture5Text": "كل مشروع نقدمه يساعد المؤسسين على النمو والمستثمرين على الاستثمار بشكل أذكى.",
      "about.manifestoTitle": "بيان كونكت",
      "about.manifesto1": "مرحباً بك في عصر \"البناء\".",
      "about.manifesto2": "البناة لا يحلمون فقط.",
      "about.manifesto3": "البناة يصممون.",
      "about.manifesto4": "البناة ينفذون.",
      "about.manifesto5": "البناة يجمعون التمويل.",
      "about.manifesto6": "البناة هم الجيل الجديد من المؤسسين — منضبطون ومعتمدون على البيانات وجريئون بما يكفي لتحويل الأفكار الجريئة إلى حقائق قابلة للاستثمار.",
      "about.manifesto7": "لم يكن رأس المال قريباً جداً من قبل.",
      "about.manifesto8": "لم تكن الأدوات والرؤى والشبكات العالمية متاحة بهذا الشكل من قبل.",
      "about.manifesto9": "لم يكن المؤسسون ممكنين بهذا الشكل للبناء بوضوح — وليس بفوضى.",
      "about.manifesto10": "التكنولوجيا تعيد كتابة قواعد جمع التمويل.",
      "about.manifesto11": "وكونكت هو المكان الذي يلتقي فيه الطموح بالهيكل.",
      "about.manifesto12": "افعل شركتك الناشئة التي تغير قواعد اللعبة.",
      "about.manifesto13": "افعل جولة التمويل التالية.",
      "about.manifesto14": "افعل قصتك الجاهزة للمستثمرين.",
      "about.manifesto15": "افعل نموك المنضبط.",
      "about.manifesto16": "الآن أكثر من أي وقت مضى، هذا هو وقت البناء.",
      "about.manifesto17": "وكونكت تساعد البناة في جميع أنحاء الأسواق الناشئة على جمع التمويل بذكاء والتوسع بسرعة والبقاء مركزين.",
      "about.manifesto18": "هذه مهمتنا.",
      "about.manifesto19": "كونكت تضفي الطابع الديمقراطي على الوصول إلى جاهزية رأس المال — تحويل كيفية تعاون المؤسسين والمستثمرين والمستشارين لتنمية الأعمال المستدامة.",
      "about.manifesto20": "ندعو البناة حول العالم للتوقف عن التخمين — والبدء في النمو.",
      "about.manifesto21": "معاً، لا يوجد شيء لا يمكننا بناؤه.",
      "about.manifesto22": "دعنا نتواصل معك!",
      "about.ctaTitle": "فصلك التالي يبدأ هنا.",
      "about.ctaText": "في كونكت، نساعد في تشكيل كيفية جمع المؤسسين للتمويل، واستثمار المستثمرين، وتوسع الشركات الناشئة — عبر الأسواق الناشئة وما بعدها. نحن نبني مستقبل رأس المال.",
      "about.ctaButton": "انضم الآن!",



      // Careers Page
      "careers.heroTitle": "الوظائف في كونكت",
      "careers.heroSubtitle": "ابنِ مستقبل جمع التمويل والاستراتيجية والنمو.",
      "careers.heroText1": "في كونكت، نحن في مهمة لإعادة تعريف كيفية جمع المؤسسين لرأس المال وبناء أعمال دائمة.",
      "careers.heroText2": "نجمع بين الرؤى المدعومة بالذكاء الاصطناعي والاستراتيجية المعتمدة على البيانات والاستشارات المتخصصة لمساعدة رواد الأعمال على أن يصبحوا جاهزين للمستثمرين، وإغلاق الجولات بشكل أسرع، والتوسع بثقة.",
      "careers.heroText3": "إذا كنت شغوفاً بالابتكار والتكنولوجيا وتمكين المؤسسين — فهذا هو المكان المناسب.",
      "careers.viewRoles": "عرض الوظائف المتاحة",
      "careers.lifeTitle": "الحياة في كونكت",
      "careers.lifeSubtitle": "نحن لا نعمل فقط على الشركات الناشئة — بل نعمل مثلها. مرن وسريع ومركز على النمو.",
      "careers.card1": "اعمل عن بُعد أو من مراكز في دبي وأبو ظبي أو لندن",
      "careers.card2": "تعاون مباشرة مع المؤسسين والمستثمرين والمبتكرين",
      "careers.card3": "تعلم من كبار خبراء الصناعة ووسع مجموعة مهاراتك",
      "careers.card4": "كن جزءاً من مهمة تحدث تأثيراً اقتصادياً واجتماعياً حقيقياً",
      "careers.departmentsTitle": "الأقسام التي ننميها",
      "careers.departmentsText": "نحن نوسع فريقنا في عدة مجالات رئيسية:",
      "careers.dept1Title": "استشارات الأعمال والاستراتيجية",
      "careers.dept1Text": " – توجيه الشركات الناشئة بالاستراتيجية ورؤية السوق والوضوح المالي.",
      "careers.dept2Title": "الاستشارات المالية والاستثمارية",
      "careers.dept2Text": " – تصميم النماذج والتقييمات ومواد المستثمرين التي تغلق الجولات.",
      "careers.dept3Title": "المنتج والتكنولوجيا",
      "careers.dept3Text": " – بناء أنظمة الذكاء الاصطناعي والأدوات الرقمية التي تدعم جمع التمويل الأذكى.",
      "careers.dept4Title": "التسويق والنمو",
      "careers.dept4Text": " – دفع المشاركة من خلال السرد القصصي واستراتيجية العلامة التجارية والحملات الرقمية.",
      "careers.dept5Title": "الشراكات والنظام البيئي",
      "careers.dept5Text": " – التعاون مع رأس المال الاستثماري والمسرعات والمؤسسات لتوسيع نطاق تأثيرنا.",
      "careers.valuesTitle": "قيمنا",
      "careers.value1": "الوضوح",
      "careers.value1Text": " — البيانات والانضباط يوجهان كل قرار.",
      "careers.value2": "الإبداع",
      "careers.value2Text": " — نبني حلولاً أذكى من خلال التصميم والتكنولوجيا.",
      "careers.value3": "المجتمع",
      "careers.value3Text": " — معاً، نجعل نظام الشركات الناشئة أقوى.",
      "careers.value4": "الالتزام",
      "careers.value4Text": " — نذهب إلى الميل الإضافي من أجل مؤسسينا — ولبعضنا البعض.",
      "careers.ctaTitle": "شيء عظيم يبدأ هنا.",
      "careers.ctaText": "انضم إلى فريق حيث يخلق عملك تأثيراً قابلاً للقياس — للمؤسسين والمستثمرين ومستقبل التمويل. دعونا نبني نظاماً بيئياً أذكى وأكثر عدلاً لجمع التمويل معاً.",
      "careers.ctaEmail": "أرسل سيرتك الذاتية إلى careers@conncct.com",

      // Contact Page
      "contact.title": "اتصل بنا!",
      "contact.subtitle": "نحب أن نسمع منك. سواء كان لديك سؤال أو تحتاج إلى دعم أو ترغب في استكشاف كيف يمكن لـ كونكت تسريع رحلة عملك، فريقنا هنا لمساعدتك.",
      "contact.officeTitle": "عنوان المكتب",
      "contact.address1": "دبي كريك هاربر",
      "contact.address2": "البرج الجنوبي 2، مكتب 1303",
      "contact.address3": "دبي، الإمارات العربية المتحدة",
      "contact.emailTitle": "البريد الإلكتروني",
      "contact.emailAddress": "support@conncct.com",
      "contact.phoneTitle": "الهاتف / واتساب",
      "contact.phoneNumber": "+971 50 164 1651",
      "contact.hoursTitle": "ساعات العمل",
      "contact.hours1": "الأحد – الجمعة: 9:00 صباحاً – 7:00 مساءً (بتوقيت الخليج)",
      "contact.hours2": "السبت: مغلق",
      "contact.touchTitle": "تواصل معنا",
      "contact.touchText1": "لفرص الشراكة أو استفسارات الاستثمار أو طلبات وسائل الإعلام، يرجى التواصل عبر بريدنا الإلكتروني أعلاه.",
      "contact.touchText2": "سيستجيب فريقنا في غضون 24-48 ساعة.",

      // Pricing Navigation and Modal
      "pricing.title": "الأسعار",
      "pricing.subtitle": "اختر الخطة المثالية لرحلة جمع التمويل",
      "pricing.billingToggle": "دورة الفوترة",
      "pricing.yearly": "سنوي",
      "pricing.monthly": "شهري",
      "pricing.unlimited": "غير محدود",
      "pricing.mostPopular": "الأكثر شعبية",
      "pricing.getStarted": "ابدأ الآن",
      "pricing.deliverables": "المخرجات",
      "pricing.featureTable": "جدول المميزات",
      "pricing.featureTableSubtitle": "اختر الخطة المثالية لاحتياجات عملك",
      "pricing.selected": "محدد",
      "pricing.continue": "المتابعة إلى الدفع",
      "pricing.limit.100": "١٠٠",
      "pricing.selectPlan": "يرجى اختيار خطة",

      // Starter Pack
      "pricing.starterPack": "الباقة الأساسية",
      "pricing.starterDuration": "2-3 أسابيع",
      "pricing.starterTag": "أساسيات المستثمر",
      "pricing.starterDesc": "للمؤسسين في المراحل المبكرة الذين يحتاجون إلى تصحيح الأساسيات",
      "pricing.starter.price": "2,999 دولار",
      "pricing.starter.item1": "عرض تقديمي (10-12 شريحة، موجه للمستثمرين)",
      "pricing.starter.item2": "خطة مالية (توقعات لمدة 3 سنوات، نموذج مبني على المحركات)",
      "pricing.starter.item3": "خطة عمل (مختصرة: Lean Canvas + ملخص سردي، ~8-10 صفحات)",

      // Growth Pack
      "pricing.growthPack": "باقة النمو",
      "pricing.growthDuration": "4-8 أسابيع",
      "pricing.growthTag": "جاهز للاستثمار",
      "pricing.growthDesc": "للشركات الناشئة التي تستعد لجمع التمويل الأولي/السلسلة A",
      "pricing.growth.price": "6,999 دولار",
      "pricing.growth.item1": "عرض تقديمي (جاهز للمستثمرين، ماليات متكاملة)",
      "pricing.growth.item2": "نموذج مالي (5 سنوات، مبني على سيناريوهات، CAPEX/OPEX، مؤشرات الأداء)",
      "pricing.growth.item3": "خطة عمل (النسخة الكاملة: الاستراتيجية، السوق، الماليات، ~10 صفحات)",
      "pricing.growth.item4": "تقرير تقييم الشركة (DCF، نظراء، مضاعفات)",
      "pricing.growth.item5": "إعداد غرفة بيانات المستثمرين (مجلدات، بيانات مالية، قائمة مراجعة وثائق العناية الواجبة)",

      // Scale Pack
      "pricing.scalePack": "باقة التوسع",
      "pricing.scaleDuration": "8-12 أسبوعاً",
      "pricing.scaleTag": "صندوق العناية الواجبة الكامل",
      "pricing.scaleDesc": "للشركات التي تستهدف المستثمرين المؤسسيين أو صناديق رأس المال الجريء أو تمويل التوسع",
      "pricing.scale.price": "11,999 دولار",
      "pricing.scale.oldPrice": "13,999 دولار",
      "pricing.scale.savings": "3,999 دولار / أربع دفعات (سنوي)",
      "pricing.scale.item1": "عرض تقديمي (مُصمَّم ومُخصَّص لأنواع مستثمرين مختلفة)",
      "pricing.scale.item2": "نموذج مالي متقدم (متعدد السيناريوهات، السعر × الحجم، التوسع الإقليمي)",
      "pricing.scale.item3": "خطة عمل شاملة (معمّقة، 40+ صفحة، تشمل استراتيجية التمويل)",
      "pricing.scale.item4": "تقرير تقييم الشركة (متعدد الطرق، تحليل الحساسية)",
      "pricing.scale.item5": "غرفة بيانات كاملة للمستثمرين (ماليات، سياسات، مؤشرات أداء، وثائق حوكمة، امتثال قانوني)",
      "pricing.scale.item6": "مراجعة جاهزية العناية الواجبة (تحليل الفجوات + خطة تصحيحية)",

      // Feature Table
      "pricing.taskManagement": "إدارة المهام",
      "pricing.customWorkflows": "سير عمل مخصصة",
      "pricing.projectPlanning": "تخطيط المشاريع",
      "pricing.timeTracking": "تتبع الوقت",
      "pricing.visualization": "التصور البياني",
      "pricing.ganttCharts": "مخططات غانت",
      "pricing.agileSupport": "دعم منهجية أجايل",
      "pricing.reportingAnalytics": "التقارير والتحليلات",
      "pricing.teamDashboards": "لوحات فريق وفردية",
      "pricing.integrations": "التكاملات",
      "pricing.docManagement": "إدارة المستندات",
      "pricing.clientCollaboration": "تعاون العملاء",
      "pricing.mobileApp": "تكامل تطبيق الجوال",

      // FAQ
      "pricing.faqTitle": "الأسئلة الشائعة",
      "pricing.faqSubtitle": "الأسئلة الشائعة مرتبة حسب الشعبية. تذكر أنه إذا لم يلتزم الزائر بالدعوة إلى اتخاذ إجراء، فقد لا تزال لديه أسئلة (شكوك) يمكن الإجابة عليها.",
      "pricing.faqQ1": "ما هي كونكت؟",
      "pricing.faqA1": "كونكت هي منصة شاملة تساعد الشركات الناشئة والشركات الصغيرة والمتوسطة على الاستعداد لجمع التمويل، وإدارة غرف البيانات الخاصة بها، والوصول إلى استشارات الخبراء في مجالات التمويل والقانون والاستراتيجية — كل ذلك في مكان واحد.",
      "pricing.faqQ2": "هل أحتاج إلى أن أكون شركة راسخة لاستخدام كونكت؟",
      "pricing.faqA2": "لا، ليس بالضرورة. تعمل كونكت مع كل من الشركات الناشئة في مراحلها المبكرة والشركات الصغيرة والمتوسطة النامية. سواء كنت تجمع جولتك الأولى من التمويل أو تتوسع للسلسلة A، يمكن لمستشارينا مساعدتك على أن تكون جاهزاً للمستثمرين.",
      "pricing.faqQ3": "لماذا يجب أن أستخدم كونكت لجمع رأس المال؟",
      "pricing.faqA3": "تجمع كونكت كل ما يريده المستثمرون تحت سقف واحد — عرضك التقديمي، التقييم، مؤشرات الأداء الرئيسية، والماليات — مع ربطك بالمستثمرين والمستشارين الموثوقين.",
      "pricing.faqQ4": "ما المعلومات التي تحتاجها مني للبدء؟",
      "pricing.faqA4": "نحتاج فقط اسم شركتك، مرحلة العمل، وأهداف التمويل. بمجرد التسجيل، سيرشدك نموذج الإعداد الخاص بنا لإنشاء ملف الاستعداد وإعداد غرفة البيانات الخاصة بك.",
      "pricing.faqQ5": "ما هي كونكت للاستشارات؟",
      "pricing.faqA5": "تمنحك كونكت للاستشارات الوصول إلى مديري شؤون مالية ومستشارين قانونيين واستراتيجيين ذوي خبرة يصممون حلولاً تناسب أهداف نموك.",
      "pricing.faqQ6": "هل يمكن لكونكت مساعدتي في خطة العمل والتقييم؟",
      "pricing.faqA6": "بالتأكيد. يساعدك فريقنا في بناء خطة عمل مفصلة وتوقعات مالية ونموذج تقييم يتوافق مع توقعات المستثمرين في مجالك.",
      "pricing.faqQ7": "لمن صُممت كونكت؟",
      "pricing.faqA7": "صُممت كونكت للمؤسسين ورواد الأعمال وأصحاب الأعمال الصغيرة الذين يرغبون في جمع رأس المال بكفاءة والبقاء ملتزمين والتوسع استراتيجياً.",
      "pricing.faqQ8": "هل بياناتي آمنة على كونكت؟",
      "pricing.faqA8": "نعم. جميع بيانات عملك والمستثمرين مشفرة ومخزنة في غرفة البيانات الآمنة الخاصة بك، ولا يمكن الوصول إليها إلا من قبلك والشركاء المعتمدين.",

      "pricing.fourPayments": "أربع دفعات | سنوي",
      "pricing.save": "وفر 2,000 دولار*",
      "pricing.back": "رجوع",
      "pricing.next": "التالي",
      "pricing.modal.title": "تشعر بالحيرة؟",
      "pricing.modal.description": "احجز مكالمة تجريبية مجانية اليوم ودع خبراءنا يرشدونك.",
      "pricing.modal.continue": "متابعة",
      "pricing.modal.bookDemo": "احجز عرضاً توضيحياً",

      // Raise Capital Page
      "raiseCapital.title": "جمع رأس المال",
      "raiseCapital.subtitle": "سيساعدنا المبلغ الذي تريد جمعه في العثور على المستثمر المناسب.",
      "raiseCapital.q1.title": "كم تتطلع لجمعه؟",
      "raiseCapital.q1.subtitle": "سيساعدنا المبلغ الذي تريد جمعه في العثور على المستثمر المناسب.",
      "raiseCapital.q1.placeholder": "أدخل المبلغ هنا",
      "raiseCapital.q2.title": "ما هي الجولة التي تجمع لها رأس المال؟",
      "raiseCapital.q2.subtitle": "يساعدنا هذا في مطابقتك مع ملفات المستثمرين وأحجام الشيكات المناسبة.",
      "raiseCapital.q2.preSeed": "ما قبل التمويل الأولي",
      "raiseCapital.q2.seed": "التمويل الأولي",
      "raiseCapital.q2.seriesA": "السلسلة A",
      "raiseCapital.q2.seriesB": "السلسلة B+",
      "raiseCapital.q2.notSure": "غير متأكد بعد",
      "raiseCapital.q3.title": "ما نوع رأس المال الذي تجمعه؟",
      "raiseCapital.q3.equity": "حصص ملكية (جولة مسعرة)",
      "raiseCapital.q3.convertible": "قابل للتحويل (SAFE/سند)",
      "raiseCapital.q3.debt": "دين",
      "raiseCapital.q3.notSure": "غير متأكد بعد",
      "raiseCapital.q4.title": "هل لديك تقييم مستهدف أو حد أقصى؟ (اختياري)",
      "raiseCapital.q4.subtitle": "مشاركة التقييم المستهدف قبل المال (أو حد SAFE الأقصى) يساعدنا في مطابقتك مع المستثمرين المناسبين وتجنب المحادثات غير المتوافقة.",
      "raiseCapital.q4.placeholder": "أدخل المبلغ هنا",
      "raiseCapital.q5.title": "أخبرنا عن شركتك",
      "raiseCapital.q5.subtitle": "نحتاج فقط إلى بعض التفاصيل الإضافية للمتابعة.",
      "raiseCapital.q5.placeholder": "أخبرنا المزيد عن شركتك",
      "raiseCapital.q6.placeholder": "موقعك الإلكتروني",
      "raiseCapital.backToDashboard": "← العودة إلى لوحة القيادة",
      "raiseCapital.continue": "متابعة",
      "raiseCapital.saving": "جاري الحفظ...",
      "raiseCapital.alert.fillRequired": "يرجى ملء جميع الحقول المطلوبة",
      "raiseCapital.alert.loginFirst": "يرجى تسجيل الدخول أولاً",
      "raiseCapital.alert.saveFailed": "فشل الحفظ. يرجى المحاولة مرة أخرى.",

      // Partner Page
      "partner.heroTitle": "كن شريكاً مع",
      "partner.heroTitleBrand": "كونكت",
      "partner.heroText1": "اكسب ما يصل إلى 5000 دولار لكل إحالة عن طريق تقديم الشركات الناشئة والمؤسسين ورواد الأعمال الجدد إلى كونكت — المنصة الشاملة لإعداد الأعمال وجمع التمويل والتحول الرقمي.",
      "partner.heroText2": "اختر نموذج الشراكة الذي يناسبك.",
      "partner.heroText3": "أحل كونكت. ابدأ في الكسب. إنه سهل حقاً.",
      "partner.becomePartner": "كن شريكاً",
      "partner.commissionTitle": "عمولاتك",
      "partner.joinTitle": "انضم إلى المئات الذين يكسبون دخلاً سلبياً شهرياً كشريك كونكت",
      "partner.rightFitTitle": "هل أنت مناسب؟",
      "partner.fitCard1Title": "أفضل شركائنا يدعمون هذه الأنظمة البيئية:",
      "partner.fitCard1Item1": "رائدات الأعمال",
      "partner.fitCard1Item2": "شركات التسويق الرقمي",
      "partner.fitCard1Item3": "المحاسبون والمستشارون الماليون",
      "partner.fitCard1Item4": "مسرعات الشركات الناشئة والمستشارون التجاريون",
      "partner.fitCard2Title": "أفضل شركائنا يريدون توسيع نطاق وصولهم وهم:",
      "partner.fitCard2Item1": "منشئو المحتوى والمعلمون",
      "partner.fitCard2Item2": "بناة المجتمع والمؤثرون",
      "partner.fitCard2Item3": "مستشارو الاستثمار وموجهو الشركات الناشئة",
      "partner.earningTitle": "كيف يبدو كسب ما يصل إلى 5000 دولار لكل إحالة؟",
      "partner.earningItem1": "يولد أفضل الشركاء أكثر من 10000 دولار شهرياً عن طريق إحالة المؤسسين والشركات الناشئة والشركات إلى منصة كونكت.",
      "partner.earningItem2": "اكسب ما يصل إلى 50٪ عمولات — ما يصل إلى 5000 دولار لكل إحالة ناجحة.",
      "partner.earningItem3": "تعاون مباشرة مع فريق قيادة كونكت لبناء خطوط نمو وشراكات طويلة الأجل.",
      "partner.earningQuote": "انضم إلى شبكة شركاء كونكت — حيث تلتقي الخبرة بالفرصة.",
      "partner.stepsTitle": "كن شريكاً مع",
      "partner.stepsSubtitle": "في 3 خطوات بسيطة",
      "partner.step1Title": "انضم إلى شبكة شركاء كونكت",
      "partner.step1Text": "كن شريكاً رسمياً وساعد المؤسسين والمستشارين والشركات على اكتشاف كونكت.",
      "partner.step1Item1": "الوصول المبكر إلى مجتمعنا",
      "partner.step1Item2": "ابق على اطلاع بالأدوات القادمة وميزات الشركاء",
      "partner.step2Title": "الوصول إلى مجموعة الشريك الخاصة بك",
      "partner.step2Text": "سنرسل لك جميع الأساسيات للبدء.",
      "partner.step2Item1": "عروض تقديمية ومرئيات وقوالب الإعداد",
      "partner.step2Item2": "إرشادات حول كيفية تقديم كونكت إلى شبكتك",
      "partner.step2Item3": "راسلنا عبر البريد الإلكتروني على referral@conncct.com لتلقي مجموعتك.",
      "partner.step3Title": "اكسب وانمو",
      "partner.step3Subtitle": "(رموز الإحالة قريباً)",
      "partner.step3Text": "ستكون من بين الأوائل الذين يتلقون رمز شريك فريد ولوحة معلومات بمجرد إطلاق المرحلة الثانية.",
      "partner.step3Item1": "تتبع الإحالات والأداء",
      "partner.step3Item2": "استمتع بمكافآت الشركاء الحصرية والعمولات",
      "partner.consultingTitle": "روّج لخدمات استشارات كونكت",
      "partner.consultingText": "كشريك كونكت، يمكنك إحالة العملاء إلى حلولنا الاستشارية — مساعدة الشركات الناشئة والشركات الصغيرة والمتوسطة على النمو بشكل أذكى مع كسب العمولات على طول الطريق.",
      "partner.consultingCard1Badge": "الأعمال",
      "partner.consultingCard1Title": "استراتيجية ونمو الأعمال",
      "partner.consultingCard1Text": "نساعد المؤسسين على تشكيل نماذج أعمال قابلة للتوسع، وتحسين استراتيجيات الذهاب إلى السوق، وتحديد معالم نمو واضحة — تحويل الأفكار إلى مشاريع مستدامة.",
      "partner.consultingCard2Badge": "مالي",
      "partner.consultingCard2Title": "التخطيط المالي والاستشارات",
      "partner.consultingCard2Text": "تدعم كونكت الشركات في إعداد الميزانية والتنبؤ والهيكلة المالية — مما يضمن أنها جاهزة للمستثمرين ومبنية للنجاح طويل الأجل.",
      "partner.consultingCard3Badge": "جمع التمويل",
      "partner.consultingCard3Title": "جمع التمويل والجاهزية للمستثمرين",
      "partner.consultingCard3Text": "نعد الشركات الناشئة لجمع رأس المال بعروض تقديمية مخصصة وتقييمات والوصول إلى شبكة المستثمرين المتوسعة.",
      "partner.faqTitle": "الأسئلة الشائعة",
      "partner.faqSubtitle": "الأسئلة الشائعة مرتبة حسب الشعبية. تذكر أنه إذا لم يلتزم الزائر بالدعوة إلى اتخاذ إجراء، فقد لا تزال لديه أسئلة (شكوك) يمكن الإجابة عليها.",
      "partner.faqQ1": "ما هو برنامج شركاء كونكت؟",
      "partner.faqQ2": "ما هي طرق الشراكة مع كونكت؟",
      "partner.faqQ3": "ما نوع العملاء الذين يمكنني إحالتهم؟",
      "partner.faqQ4": "ما المكافآت التي يحصل عليها الشركاء؟",
      "partner.faqQ5": "كيف يمكنني الانضمام؟",
      "partner.faqA1.intro": "يمكّن برنامج شركاء كونكت المهنيين — بما في ذلك المستشارين والمحاسبين والحاضنات والمستشارين — من إحالة الشركات الناشئة والشركات إلى منصتنا. يمكن للشركاء ربط العملاء بخدمات كونكت الاستشارية والمالية والرقمية مع كسب عمولات على كل تعامل ناجح.",
      "partner.faqA1.simplifies": "يبسط كونكت الوصول إلى:",
      "partner.faqA1.bullet1": "إعداد الأعمال والاستشارات الامتثالية",
      "partner.faqA1.bullet2": "التخطيط المالي واستشارات الاستعداد للمستثمرين",
      "partner.faqA1.bullet3": "التحول الرقمي وخدمات التسويق (عبر Tech-X.co)",
      "partner.faqA2.intro": "يمكنك الشراكة مع كونكت بطريقتين رئيسيتين:",
      "partner.faqA2.referral": "شريك الإحالة",
      "partner.faqA2.referralDesc": "— قدّم العملاء إلى كونكت واكسب عمولة عن كل تسجيل ناجح.",
      "partner.faqA2.consulting": "شريك استشاري (قريباً)",
      "partner.faqA2.consultingDesc": "— أسعار الجملة، وفرص العلامة التجارية المشتركة، والوصول إلى لوحات تحكم العملاء للاستشارات والدعم بعلامة تجارية بيضاء.",
      "partner.faqA2.enterprise": "يمكن لشركاء المؤسسات والحاضنات طلب حزم تعاون مخصصة بناءً على الحجم والوصول إلى السوق.",
      "partner.faqA3.intro": "تشمل إحالاتنا المثالية:",
      "partner.faqA3.bullet1": "الشركات الناشئة والشركات الصغيرة والمتوسطة التي تسعى للحصول على استشارات أو دعم لجمع التمويل",
      "partner.faqA3.bullet2": "المؤسسون الذين يعدون مواد المستثمرين أو الخطط المالية",
      "partner.faqA3.bullet3": "الشركات التي تسعى للتحول الرقمي أو التشغيلي",
      "partner.faqA4": "اكسب حتى 50٪ عمولة وافتح مزايا الشركاء الحصرية مع نمو البرنامج — بما في ذلك التسويق المشترك ودعوات الأحداث والوصول المبكر إلى الأدوات الجديدة.",
      "partner.faqA5": "راسلنا عبر البريد الإلكتروني على referral@conncct.com للبدء.",
      "partner.ctaTitle": "دعنا ننمو ونكسب معاً",
      "partner.ctaText1": "كن شريكاً مع كونكت وساعد المؤسسين والشركات الناشئة والشركات على النمو بشكل أذكى — مع الكسب على طول الطريق.",
      "partner.ctaText2": "انضم إلى شبكة عالمية من المستشارين والمستشارين والمهنيين الذين يشكلون مستقبل الأعمال والتمويل.",
      "partner.ctaButton": "كن شريكاً تحدث إلى المبيعات",

      // Demo Pages
      "demo.login": "تسجيل الدخول",
      "demo.title1": "دعنا نطلعك على كونكت، إنها رائعة حقاً.",
      "demo.tag1": "جوجل ميت أو زوم",
      "demo.tag2": "15 دقيقة",
      "demo.tag3": "مجاني",
      "demo.firstName": "الاسم الأول",
      "demo.lastName": "اسم العائلة",
      "demo.email": "أدخل البريد الإلكتروني",
      "demo.terms": "بالمتابعة، أوافق على",
      "demo.termsLink": "الشروط والأحكام الخاصة بـ كونكت",
      "demo.next": "التالي ←",
      "demo.back": "→ رجوع",
      "demo.title2": "نحن متحمسون لعرض المنصة لك!",
      "demo.helpWith": "ما الذي تحتاج المساعدة فيه؟",
      "demo.helpOption1": "جمع رأس المال",
      "demo.helpOption2": "بناء خطة عمل",
      "demo.helpOption3": "تحسين درجة جاهزية المستثمر",
      "demo.helpOption4": "الاستشارات المالية",
      "demo.companyName": "اسم الشركة",
      "demo.discussionTopic": "ما الذي تريد مناقشته؟",
      "demo.submit": "← إرسال",
      "demo.submitting": "جارٍ الإرسال...",
      "demo.fillAllFields": "يرجى ملء جميع الحقول",
      "demo.missingInfo": "معلومات مفقودة من الخطوة السابقة",
      "demo.cannotConnect": "لا يمكن الاتصال بالخادم. يرجى التحقق مما إذا كان الخادم الخلفي قيد التشغيل.",

      // Goal Assessment Form
      "goal.step1": "تقييم الهدف",
      "goal.step2": "درجة جاهزية المستثمر",
      "goal.step3": "استكشاف الأدوات",
      "goal.q1": "ما هو تركيزك الأول الآن؟",
      "goal.q1opt1": "جمع الأموال",
      "goal.q1opt2": "بناء المنتج",
      "goal.q1opt3": "زيادة المبيعات",
      "goal.q1opt4": "إعداد العمليات",
      "goal.q2": "أين تذهب معظم وقتك؟",
      "goal.q2opt1": "العمل مع المستثمرين",
      "goal.q2opt2": "بناء المنتج",
      "goal.q2opt3": "التسويق والمبيعات",
      "goal.q2opt4": "العمليات والقانون",
      "goal.q3": "كيف سيبدو الفوز خلال 6 أشهر بالنسبة لك؟",
      "goal.q3opt1": "جولة تمويل",
      "goal.q3opt2": "منتج حي",
      "goal.q3opt3": "نمو الإيرادات",
      "goal.q3opt4": "عمليات نظيفة",
      "goal.q4": "ما الذي يعيقك أكثر اليوم؟",
      "goal.q4opt1": "معرفة المستثمرين",
      "goal.q4opt2": "البناء والاختبار",
      "goal.q4opt3": "نمو العملاء",
      "goal.q4opt4": "الإعداد القانوني",
      "goal.q5": "ما هي خطوة واحدة يمكنك اتخاذها هذا الأسبوع؟",
      "goal.q5opt1": "جمع الأموال الآن",
      "goal.q5opt2": "بناء الميزات",
      "goal.q5opt3": "إطلاق التسويق",
      "goal.q5opt4": "توظيف مدير عمليات",
      "goal.back": "→ رجوع",
      "goal.continue": "← متابعة",
      "goal.saving": "جارٍ الحفظ...",
      "goal.modalTitle": "هل أنت مستعد لفتح درجة جاهزية المستثمر الآن؟",
      "goal.modalYes": "نعم",
      "goal.modalYesDesc": "سيزيد من فرصتك لزيادة رأس المال",
      "goal.modalNo": "لا",
      "goal.modalNoDesc": "يمكنك أخذها لاحقاً من لوحة التحكم",
      "goal.modalNext": "التالي",
      "goal.alertAllQuestions": "أجب على جميع الأسئلة",
      "goal.alertFillAll": "أجب على جميع الأسئلة",
      "goal.alertLogin": "يرجى تسجيل الدخول أولاً",
      "goal.alertSaveFailed": "فشل الحفظ. يرجى المحاولة مرة أخرى.",
      "goal.alertForm1First": "يرجى إكمال النموذج 1 أولاً",

      // Form 1 - Investor Readiness
      "form1.q1": "ما هو الوقت المتبقي لشركتك حالياً؟",
      "form1.q1opt1": "أقل من 6 أشهر",
      "form1.q1opt2": "6-12 شهراً",
      "form1.q1opt3": "12-18 شهراً",
      "form1.q1opt4": "18-24 شهراً",
      "form1.q1opt5": "أكثر من 24 شهراً",
      "form1.q2": "كم حجم رأس المال الذي تخطط لجمعه؟",
      "form1.q2placeholder": "أدخل المبلغ (مثال: $500,000)",
      "form1.q3": "ما هي تكلفة التشغيل السنوية (معدل الحرق)؟",
      "form1.q3placeholder": "أدخل المبلغ (مثال: $300,000)",
      "form1.q4": "ما هو تقييم شركتك الحالي؟",
      "form1.q4placeholder": "أدخل المبلغ (مثال: $2,000,000)",
      "form1.q5": "متى تتوقع أن تحقق إيرادات؟",
      "form1.q5opt1": "تحقق الإيرادات بالفعل",
      "form1.q5opt2": "خلال 3 أشهر",
      "form1.q5opt3": "خلال 6 أشهر",
      "form1.q5opt4": "خلال 12 شهراً",
      "form1.q5opt5": "أكثر من 12 شهراً",
      "form1.q5opt6": "غير مؤكد",
      "form1.q6": "ما هو المعلم الرئيسي القادم؟",
      "form1.q6opt1": "إطلاق المنتج",
      "form1.q6opt2": "شراكة استراتيجية",
      "form1.q6opt3": "خفض التكاليف",
      "form1.q6opt4": "جولة تمويل جديدة",
      "form1.q6opt5": "آخر",
      "form1.q7": "هل لديك أي ديون حالية؟",
      "form1.q7opt1": "نعم",
      "form1.q7opt2": "لا",
      "form1.q8": "ما نوع الديون لديك؟ (اختر جميع ما ينطبق)",
      "form1.q8opt1": "سندات قابلة للتحويل",
      "form1.q8opt2": "ديون رأسمالية",
      "form1.q8opt3": "قروض بنكية",
      "form1.q8opt4": "مستحقات متأخرة",
      "form1.q8opt5": "التزامات أخرى",

      // Form 2 - Investor Readiness Part 2
      "form2.q1": "هل لديك أي مستحقات متأخرة؟",
      "form2.q1opt1": "نعم - مبالغ صغيرة",
      "form2.q1opt2": "تحت السيطرة",
      "form2.q1opt3": "نعم - كبيرة وتؤثر على العمليات",
      "form2.q1opt4": "لا",
      "form2.q2": "ما مدى استقرار إيرادات شركتك الناشئة؟",
      "form2.q2opt1": "لا يوجد",
      "form2.q2opt2": "غير منتظم",
      "form2.q2opt3": "مستقر إلى حد ما",
      "form2.q2opt4": "مستقر في الغالب",
      "form2.q2opt5": "متكرر",
      "form2.q3": "كيف ستتفاعل إذا واجهت شركتك الناشئة انتكاسة كبيرة غير متوقعة (مثل فقدان عميل رئيسي، تأخير التمويل)؟",
      "form2.q3opt1": "تغيير الاستراتيجية",
      "form2.q3opt2": "خفض التكاليف وإطالة الوقت المتبقي",
      "form2.q3opt3": "البحث عن تمويل جسر",
      "form2.q3opt4": "إيقاف العمليات وإعادة الهيكلة",
      "form2.q4": "ما مقدار الخبرة لديك في تأسيس أو قيادة الشركات الناشئة؟",
      "form2.q4opt1": "ليس لدي خبرة",
      "form2.q4opt2": "1 إلى 5 سنوات",
      "form2.q4opt3": "6 إلى 10 سنوات",
      "form2.q4opt4": "أكثر من 10 سنوات",
      "form2.q5": "ما هي نقاط القوة ذات الصلة التي يقدمها فريقك المؤسس؟ (يمكنك اختيار واحد أو عدة خيارات)",
      "form2.q5opt1": "خروج ناجح سابق من شركة ناشئة",
      "form2.q5opt2": "قيادة في شركة سريعة النمو",
      "form2.q5opt3": "خبرة تقنية عميقة",
      "form2.q5opt4": "شبكة صناعية قوية",
      "form2.q5opt5": "سجل حافل في جمع الأموال",
      "form2.q6": "كيف تتعامل مع القرارات الاستراتيجية الرئيسية لشركتك الناشئة؟",
      "form2.q6opt1": "التحليل القائم على البيانات",
      "form2.q6opt2": "استشارة المستشارين",
      "form2.q6opt3": "اتباع الغريزة",
      "form2.q6opt4": "تأجيل القرارات",
      "form2.q7": "كيف تنظر إلى المخاطرة الكبيرة لتحقيق أهداف شركتك الناشئة؟",
      "form2.q7opt1": "أفضل تقليل المخاطر",
      "form2.q7opt2": "منفتح على المخاطر المعتدلة",
      "form2.q7opt3": "مرتاح مع المخاطر الكبيرة",
      "form2.complete": "← إكمال",

      // Sidebar
      "sidebar.dashboard": "لوحة المعلومات",
      "sidebar.company": "الشركة",
      "sidebar.profile": "الملف الشخصي",
      "sidebar.dataRoom": "غرفة البيانات",
      "sidebar.financials": "المالية",
      "sidebar.irScore": "نقاط IR",
      "sidebar.kpis": "مؤشرات الأداء",
      "sidebar.services": "الخدمات",
      "sidebar.referAndEarn": "الإحالة والكسب",
      "sidebar.settings": "الإعدادات",
      "sidebar.signOut": "تسجيل الخروج",
      "sidebar.financialHealth": "الصحة المالية",
      "sidebar.articles": "المقالات",
      "sidebar.notifications": "الإشعارات",
      "sidebar.messageAdmin": "مراسلة الإدارة",

      // User Profile Menu
      "userProfile.profileSettings": "إعدادات الملف الشخصي",
      "userProfile.language": "اللغة",
      "userProfile.analytics": "التحليلات",

      // Profile Page
      "profile.title": "الملف الشخصي",
      "profile.breadcrumb.myApplications": "طلباتي",
      "profile.yourInformation": "معلوماتك",
      "profile.fullName": "الاسم الكامل",
      "profile.name": "الاسم",
      "profile.email": "البريد الإلكتروني",
      "profile.phone": "الهاتف",
      "profile.country": "البلد",
      "profile.saveChanges": "حفظ التغييرات",
      "profile.saving": "جاري الحفظ...",
      "profile.alert.loginFirst": "يرجى تسجيل الدخول أولاً",
      "profile.alert.saveSuccess": "تم حفظ الملف الشخصي بنجاح!",
      "profile.alert.saveFailed": "فشل الحفظ. يرجى المحاولة مرة أخرى.",

      // Company Profile
      "companyProfile.title": "ملف الشركة",
      "companyProfile.yourInfo": "معلوماتك",
      "companyProfile.companyNameLabel": "اسم الشركة: [قانوني] — \"حسب الرخصة التجارية\"",
      "companyProfile.brandNameLabel": "الاسم التجاري/التشغيلي (اختياري)",
      "companyProfile.incorporationDateLabel": "تاريخ التأسيس",
      "companyProfile.registeredAddressLabel": "العنوان المسجل",
      "companyProfile.websiteLabel": "الموقع الإلكتروني",
      "companyProfile.oneLineDescLabel": "وصف من سطر واحد (140 حرف)",
      "companyProfile.longDescLabel": "وصف طويل (فقرة أو فقرتين)",
      "companyProfile.sectorLabel": "القطاع / القطاع الفرعي",
      "companyProfile.foundersLabel": "المؤسسون والفريق الرئيسي (الاسم، الدور، LinkedIn)",
      "companyProfile.saveChanges": "حفظ التغييرات",
      "companyProfile.saving": "جاري الحفظ...",
      "companyProfile.myApplications": "تطبيقاتي",
      "companyProfile.breadcrumbTitle": "ملف الشركة",
      "companyProfile.placeholder.companyName": "Johns",
      "companyProfile.placeholder.brandName": "Johns",
      "companyProfile.placeholder.date": "27/8/2005",
      "companyProfile.placeholder.address": "8 Times Sqaure",
      "companyProfile.placeholder.website": "www.example.com",
      "companyProfile.placeholder.oneLineDesc": "صف شركتك في سطر واحد",
      "companyProfile.placeholder.longDesc": "صف شركتك بمزيد من التفصيل",
      "companyProfile.placeholder.sector": "القطاع",
      "companyProfile.placeholder.founders": "المؤسسون والفريق الرئيسي (الاسم، الدور، LinkedIn)",
      "companyProfile.alert.mandatory": "يرجى ملء جميع الحقول الإلزامية.",
      "companyProfile.alert.login": "يرجى تسجيل الدخول أولاً",
      "companyProfile.alert.success": "تم حفظ ملف الشركة بنجاح!",
      "companyProfile.alert.error": "فشل الحفظ. يرجى المحاولة مرة أخرى.",

      // Data Room
      "dataRoom.title": "غرفة البيانات",
      "dataRoom.myApplications": "تطبيقاتي",
      "dataRoom.tab.companyDocuments": "وثائق الشركة",
      "dataRoom.loading": "جاري تحميل غرفة البيانات...",
      "dataRoom.noFiles": "لم يتم تحميل أي ملفات بعد",
      "dataRoom.uploading": "جاري التحميل...",
      "dataRoom.uploadFile": "تحميل ملف",
      "dataRoom.categories.pitchMaterials": "مواد العرض",
      "dataRoom.categories.pitchMaterialsDesc": "العرض التقديمي (PDF)، صفحة واحدة",
      "dataRoom.categories.financials": "البيانات المالية",
      "dataRoom.categories.financialsDesc": "حساب الأرباح والخسائر، التدفق النقدي، التوقعات",
      "dataRoom.categories.legal": "قانوني",
      "dataRoom.categories.legalDesc": "التأسيس، جدول رأس المال، قرارات المساهمين",
      "dataRoom.categories.product": "المنتج",
      "dataRoom.categories.productDesc": "فيديو تجريبي، لقطات شاشة",
      "dataRoom.alert.loadError": "فشل تحميل الملفات. يرجى المحاولة مرة أخرى.",
      "dataRoom.alert.login": "يرجى تسجيل الدخول لتحميل الملفات",
      "dataRoom.alert.uploadSuccess": "تم تحميل {{count}} ملف(ات) بنجاح!",
      "dataRoom.alert.uploadFailed": "فشل تحميل الملفات.",
      "dataRoom.alert.errorUnauthorized": "تم رفض الإذن. يرجى التأكد من تسجيل الدخول.",
      "dataRoom.alert.errorUnauthenticated": "غير مصادق. يرجى تسجيل الدخول والمحاولة مرة أخرى.",
      "dataRoom.alert.errorTimeout": "انتهت مهلة التحميل. يرجى التحقق من اتصالك بالإنترنت.",
      "dataRoom.alert.errorNetwork": "خطأ في الشبكة. يرجى الاتصال بالدعم.",
      "dataRoom.alert.errorUnknown": "خطأ غير معروف. يرجى المحاولة مرة أخرى.",
      "dataRoom.alert.loginDelete": "يرجى تسجيل الدخول لحذف الملفات",
      "dataRoom.alert.confirmDelete": "هل أنت متأكد أنك تريد حذف \"{{fileName}}\"؟",
      "dataRoom.alert.deleteSuccess": "تم حذف الملف بنجاح!",
      "dataRoom.alert.deleteFailed": "فشل حذف الملف. يرجى المحاولة مرة أخرى.",

      // Financials
      "financials.title": "البيانات المالية",
      "financials.myApplications": "تطبيقاتي",
      "financials.yourInfo": "معلوماتك المالية",
      "financials.infoText": "تساعدنا هذه الأرقام في قياس الجاهزية المالية لشركتك ومقارنتها بتوقعات المستثمرين.",
      "financials.revenueLabel": "الإيرادات (ITM - آخر 12 شهرًا):",
      "financials.revenuePlaceholder": "الإيرادات (ITM - آخر 12 شهرًا)",
      "financials.ebitdaLabel": "الأرباح قبل الفوائد والضرائب والاستهلاك والإطفاء (EBITDA):",
      "financials.ebitdaPlaceholder": "الأرباح قبل الفوائد والضرائب والاستهلاك والإطفاء (EBITDA)",
      "financials.cashOnHandLabel": "النقد المتوفر:",
      "financials.cashOnHandPlaceholder": "النقد المتوفر",
      "financials.totalEquityLabel": "إجمالي حقوق الملكية",
      "financials.totalEquityPlaceholder": "إجمالي حقوق الملكية",
      "financials.mrrLabel": "الإيرادات الشهرية المتكررة (MRR):",
      "financials.mrrPlaceholder": "الإيرادات الشهرية المتكررة (MRR)",
      "financials.saveButton": "حفظ البيانات المالية",
      "financials.saving": "جاري الحفظ...",
      "financials.alert.login": "يرجى تسجيل الدخول لحفظ البيانات المالية",
      "financials.alert.emailNotFound": "البريد الإلكتروني غير موجود. يرجى تسجيل الدخول مرة أخرى.",
      "financials.alert.success": "تم حفظ البيانات المالية بنجاح!",
      "financials.alert.fail": "فشل حفظ البيانات المالية",
      "financials.alert.error": "خطأ في حفظ البيانات المالية",

      // IR Score
      "irScore.title": "نقاط IR",
      "irScore.myApplications": "تطبيقاتي",
      "irScore.yourScore": "نقاط IR الخاصة بك",
      "irScore.totalScore": "المجموع الكلي",
      "irScore.weightedScore": "النقاط المرجحة / 100",
      "irScore.breakdown": "التفصيل حسب الفئة",
      "irScore.col.category": "الفئة",
      "irScore.col.score": "النقاط",
      "irScore.col.weight": "الوزن",
      "irScore.col.weightedScore": "النقاط المرجحة",
      "irScore.cat.companyProfile": "ملف الشركة",
      "irScore.cat.capitalReadiness": "جاهزية رأس المال",
      "irScore.cat.growthArchitecture": "هندسة النمو",
      "irScore.cat.growthFinancial": "النمو + الذكاء المالي",
      "irScore.cat.governance": "الحوكمة والهيكل",
      "irScore.loading": "جاري التحميل...",
      "irScore.alert.login": "يرجى تسجيل الدخول لعرض نقاط IR",
      "irScore.alert.emailNotFound": "البريد الإلكتروني غير موجود. يرجى تسجيل الدخول مرة أخرى.",
      "irScore.alert.error": "خطأ في جلب بيانات المستخدم",

      // IR Score Diagnostic Report Page
      "irScore.report.title": "تقرير تشخيص المؤسس",
      "irScore.report.subtitle": "تقييم شخصي لجاهزيتك لرأس المال وأولويات خطواتك التالية.",
      "irScore.report.diagnosisSummary": "ملخص التشخيص",
      "irScore.report.loadingReport": "جاري تحميل تقريرك...",
      "irScore.report.downloading": "جاري التنزيل...",
      "irScore.report.downloadReport": "تنزيل التقرير (PDF)",
      "irScore.report.completeFirst": "يرجى إكمال التقييم أولاً لتنزيل تقريرك.",
      "irScore.report.noAssessment": "لم تكمل تقييم جاهزية المستثمر بعد. أكمل التقييم لرؤية تقريرك التشخيصي المفصل.",
      "irScore.report.takeAssessment": "ابدأ التقييم",
      "irScore.report.paragraph1": "أنت في نقطة البداية لرحلة جمع التمويل. هذا يعني أنك تفتقر حالياً إلى اللبنات الأساسية التي يتوقعها المستثمرون قبل النظر في تقديم رأس المال. في هذه المرحلة، قد تعمل شركتك على الرؤية والجهد المبكر بدلاً من المواد المنظمة والأرقام المُتحقق منها أو نقاط الإثبات الجاهزة للمستثمرين.",
      "irScore.report.paragraph2": "هذه ليست نقطة ضعف — إنها ببساطة إشارة إلى أن تركيزك يجب أن يكون على الأساسات، وليس على جمع التمويل. التواصل مع المستثمرين مبكراً جداً قد يضر بالفرص المستقبلية، لأن الانطباعات الأولى لها وزنها. حقيقة أنك أكملت هذا التشخيص تُظهر أنك واعٍ ومستعد لبدء وضع أساس المصداقية أمام المستثمرين.",
      "irScore.report.paragraph3": "أنت ما نسميه {{title}}: طموح، مُصمم، وفي بداية منحنى الجاهزية. هدفك الآن هو تحويل هذا الحلم إلى قصة قابلة للاستثمار.",
      "irScore.report.col.capital": "رأس المال",
      "irScore.report.col.mvp": "المنتج الأولي",
      "irScore.report.col.marketGrowth": "السوق/النمو",
      "irScore.report.col.governance": "الحوكمة",
      "irScore.report.row.dreamer": "الحالم",
      "irScore.report.row.explorer": "المستكشف",
      "irScore.report.row.builder": "الباني",
      "irScore.report.row.shaper": "المُشكّل",
      "irScore.report.row.challenger": "المتحدي",
      "irScore.report.row.trailblazer": "الرائد",
      "irScore.report.row.visionary": "الرؤيوي",
      "irScore.report.desc.1.R1": "أنت تضع أولى لبنات قصتك الاستثمارية.",
      "irScore.report.desc.1.R2": "تتعلم أساسيات جمع التمويل وخلق القيمة.",
      "irScore.report.desc.1.R3": "بدأت في تجميع قصة رأس المال والتقييم.",
      "irScore.report.desc.1.R4": "لديك مسودة قصة؛ حان وقت صقلها.",
      "irScore.report.desc.1.R5": "موادك قوية، تحتاج فقط للتلميع.",
      "irScore.report.desc.1.R6": "أنت مقنع للمستثمرين؛ الخطوة التالية هي الزخم.",
      "irScore.report.desc.1.R7": "قصة استثمارية ملهمة؛ قابلة للتمويل ومستعدة للمستقبل.",
      "irScore.report.desc.2.R1": "أنت تُشكّل رؤيتك الأولى إلى واقع.",
      "irScore.report.desc.2.R2": "تختبر أفكاراً جريئة من خلال التجارب.",
      "irScore.report.desc.2.R3": "تحوّل الرسومات إلى نماذج أولية.",
      "irScore.report.desc.2.R4": "منتجك يتشكل؛ التحسين هو الخطوة التالية.",
      "irScore.report.desc.2.R5": "بناؤك شبه جاهز لإبهار المستخدمين.",
      "irScore.report.desc.2.R6": "لديك منتج مُتحقق منه؛ جاهز للتوسع.",
      "irScore.report.desc.2.R7": "منتج مميز بعامل إبهار.",
      "irScore.report.desc.3.R1": "أنت تتخيل عملاءك الأوائل.",
      "irScore.report.desc.3.R2": "تبحث عن فرص الجذب الأولى.",
      "irScore.report.desc.3.R3": "تجمع القنوات والمبيعات المبكرة.",
      "irScore.report.desc.3.R4": "العملاء يأتون؛ ركّز الآن على الاحتفاظ بهم.",
      "irScore.report.desc.3.R5": "دليل الإيرادات قيد التنفيذ، التوسع قريباً.",
      "irScore.report.desc.3.R6": "جذب الإيرادات قوي؛ التوسع ينتظر.",
      "irScore.report.desc.3.R7": "محرك نمو يُعجب به الأقران والمستثمرون.",
      "irScore.report.desc.4.R1": "أنت تبدأ في رسم قواعد لعبتك.",
      "irScore.report.desc.4.R2": "فضولي حول إنشاء الأساس الصحيح.",
      "irScore.report.desc.4.R3": "خطوات أولى في هيكلة الأسهم والوثائق.",
      "irScore.report.desc.4.R4": "أُطر عمل مبكرة موجودة؛ اجعلها أقوى.",
      "irScore.report.desc.4.R5": "الحوكمة تعمل، تحتاج ضبطاً دقيقاً.",
      "irScore.report.desc.4.R6": "الأنظمة موثوقة؛ مهيأة للتوسع.",
      "irScore.report.desc.4.R7": "شركة نموذجية بهيكل مثالي.",
      "irScore.report.risk.criticalRisk": "خطر حرج",
      "irScore.report.risk.veryHighRisk": "خطر عالٍ جداً",
      "irScore.report.risk.highRisk": "خطر عالٍ",
      "irScore.report.risk.mediumRisk": "خطر متوسط",
      "irScore.report.risk.semiReady": "شبه جاهز",
      "irScore.report.risk.investorReady": "جاهز للمستثمرين",
      "irScore.report.risk.exceptional": "استثنائي",

      // KPIs
      "kpis.title": "مؤشرات الأداء",
      "kpis.myApplications": "تطبيقاتي",
      "kpis.yourKpis": "مؤشرات الأداء الخاصة بك",
      "kpis.infoText": "تتبع مؤشرات الأداء الرئيسية الخاصة بك لمراقبة النمو وتحديد مجالات التحسين.",

      // Unit Economics
      "kpis.unitEconomics.title": "اقتصاديات الوحدة",
      "kpis.unitEconomics.desc": "افهم القيمة والتكلفة لكل عميل.",
      "kpis.ltvLabel": "القيمة الدائمة للعميل (LTV):",
      "kpis.ltvDesc": "إجمالي الربح المتوقع من عميل نموذجي على مدار العلاقة الكاملة.\nLTV = (ARPU × هامش الربح الإجمالي) ÷ نسبة الإلغاء الشهرية",
      "kpis.ltvPlaceholder": "القيمة الدائمة للعميل (LTV)",
      "kpis.cacLabel": "تكلفة اكتساب العميل (CAC):",
      "kpis.cacDesc": "التكلفة الكاملة للمبيعات والتسويق لاكتساب عميل جديد واحد.\nCAC = (نفقات المبيعات والتسويق ÷ العملاء الجدد المكتسبين)",
      "kpis.cacPlaceholder": "تكلفة اكتساب العميل (CAC)",
      "kpis.arpuLabel": "متوسط العائد لكل مستخدم (ARPU):",
      "kpis.arpuDesc": "ARPU هو متوسط الإيرادات لكل مستخدم نشط في فترة معينة - إجمالي الإيرادات ÷ عدد المستخدمين النشطين.\nARPU = (إجمالي الإيرادات في الفترة ÷ عدد المستخدمين النشطين في الفترة)",
      "kpis.arpuPlaceholder": "متوسط العائد لكل مستخدم (ARPU)",
      "kpis.paybackLabel": "فترة الاسترداد (بالأشهر):",
      "kpis.paybackDesc": "كم شهر يستغرق استرداد الربح الإجمالي من عميل جديد لتغطية تكلفة الاكتساب؟\nفترة الاسترداد (بالأشهر) = CAC ÷ (ARPU × هامش الربح الإجمالي %)",
      "kpis.paybackPlaceholder": "فترة الاسترداد (بالأشهر)",

      // Retention & Churn
      "kpis.retention.title": "الاحتفاظ والإلغاء",
      "kpis.retention.desc": "تتبع ولاء العملاء واستقرار الإيرادات المتكررة.",
      "kpis.nrrLabel": "صافي الاحتفاظ بالإيرادات (NRR):",
      "kpis.nrrDesc": "يقيس الصافي النمو من قاعدة العملاء الحالية بعد الترقيات، التخفيضات، والإلغاء.\nNRR=(الإيرادات الشهرية المتكررة السابقة + التوسع – الانكماش – الإلغاء) ÷ الإيرادات الشهرية المتكررة السابقة",
      "kpis.nrrPlaceholder": "صافي الاحتفاظ بالإيرادات (NRR)",
      "kpis.mrrLabel": "الإيرادات الشهرية المتكررة (MRR):",
      "kpis.mrrDesc": "إجمالي إيرادات الاشتراك المتوقعة من الاشتراكات النشطة في شهر معين (باستثناء الرسوم لمرة واحدة).\nmrr:MRR = Σ (سعر الخطة × # الاشتراكات النشطة)",
      "kpis.mrrPlaceholder": "الإيرادات الشهرية المتكررة (MRR)",
      "kpis.churnRateLabel": "معدل الإلغاء الشهري (%):",
      "kpis.churnRateDesc": "نسبة الإيرادات المتكررة المفقودة في شهر بسبب الإلغاءات أو التخفيضات.\nMRR الملغى ÷ MRR الأولي",
      "kpis.churnRatePlaceholder": "معدل الإلغاء الشهري (%)",

      // Growth & Efficiency
      "kpis.growth.title": "النمو والكفاءة",
      "kpis.growth.desc": "قيم مدى فعالية توسيع إيراداتك.",
      "kpis.magicNumberLabel": "الرقم السحري:",
      "kpis.magicNumberDesc": "يشير إلى مدى كفاءة نفقات المبيعات والتسويق في الربع الماضي في التحول إلى إيرادات سنوية متكررة جديدة.\n(ARR_t - ARR_(t-1)) * 4) ÷ S&M_(t-1)",
      "kpis.magicNumberPlaceholder": "الرقم السحري",
      "kpis.burnMultipleLabel": "مضاعف الحرق:",
      "kpis.burnMultipleDesc": "يوضح كفاءة النقد من خلال إخبار كم دولار يتم حرقه لإضافة دولار واحد من الإيرادات السنوية المتكررة الجديدة.\nمضاعف الحرق = صافي الحرق ÷ صافي الإيرادات السنوية المتكررة الجديدة",
      "kpis.burnMultiplePlaceholder": "مضاعف الحرق",

      // Liquidity & Runway
      "kpis.liquidity.title": "السيولة والمدرج",
      "kpis.liquidity.desc": "قياس الموقف النقدي والاستدامة.",
      "kpis.netBurnLabel": "صافي الحرق (شهرياً):",
      "kpis.netBurnDesc": "صافي الخسارة النقدية للشركة شهرياً بالمعدل التشغيلي الحالي.\nصافي الحرق = التدفقات النقدية الخارجة - التدفقات النقدية الداخلة",
      "kpis.netBurnPlaceholder": "صافي الحرق (شهرياً)",
      "kpis.runwayLabel": "مدرج التمويل (بالأشهر):",
      "kpis.runwayDesc": "عدد الأشهر التي يمكن للشركة العمل فيها قبل نفاد النقد بمعدل الحرق الحالي.\nالمدرج = النقد الحالي ÷ صافي الحرق",
      "kpis.runwayPlaceholder": "مدرج التمويل (بالأشهر)",

      "kpis.saveButton": "حفظ بيانات مؤشرات الأداء",
      "kpis.saving": "جاري الحفظ...",
      "kpis.alert.login": "يرجى تسجيل الدخول لحفظ مؤشرات الأداء",
      "kpis.alert.emailNotFound": "البريد الإلكتروني غير موجود. يرجى تسجيل الدخول مرة أخرى.",
      "kpis.alert.validation": "يرجى ملء جميع حقول مؤشرات الأداء قبل الحفظ.",
      "kpis.alert.success": "تم حفظ مؤشرات الأداء بنجاح!",
      "kpis.alert.fail": "فشل حفظ مؤشرات الأداء",
      "kpis.alert.error": "خطأ في حفظ مؤشرات الأداء",



      // Admin Sidebar
      "adminSidebar.dashboard": "لوحة المعلومات",
      "adminSidebar.raisingRequest": "طلب التمويل",
      "adminSidebar.partners": "الشركاء",
      "adminSidebar.adminProfile": "ملف المسؤول",
      "adminSidebar.settings": "الإعدادات",
      "adminSidebar.sendInvitation": "إرسال دعوة",
      "adminSidebar.logout": "تسجيل الخروج",

      // Admin Page

      // Dashboard
      "dashboard.welcomeBack": "مرحباً بعودتك، {{name}}! 👋",
      "dashboard.signedIn": "تم تسجيل دخولك بنجاح",
      "dashboard.completeCheck": "أكمل فحص جاهزية المستثمر المجاني",
      "dashboard.getScoreDesc": "احصل على نقاط استثمارك، وعزز فرص التمويل، وافتح استشارات مخصصة.",
      "dashboard.complete": "أكمل",
      "dashboard.capitalReadiness": "جاهزية رأس المال",
      "dashboard.capitalReadinessDesc": "جمع التبرعات وإعداد المستثمرين: العروض التقديمية، التقييم، غرفة البيانات، ومطابقة المستثمرين.",
      "dashboard.growthArchitecture": "هندسة النمو",
      "dashboard.growthArchitectureDesc": "بناء المنتج والتحقق منه هما أهدافك الرئيسية - ركز على إكمال الحد الأدنى من المنتج القابل للتطبيق (MVP)، ووضوح خارطة الطريق، والزخم.",
      "dashboard.growthFinancial": "النمو + الذكاء المالي",
      "dashboard.growthFinancialDesc": "الانضباط المالي يقود نموك - ركز على الإيرادات، مؤشرات الأداء الرئيسية (KPIs)، والتنبؤ.",
      "dashboard.governance": "الحوكمة والهيكل",
      "dashboard.governanceDesc": "تسود الحوكمة والهيكل - التركيز على القيادة والامتثال والأنظمة.",
      "dashboard.goalAssessment": "تقييم الأهداف",
      "dashboard.irScore": "نقاط IR",
      "dashboard.totalScore": "مجموع النقاط",
      "dashboard.profileTitle": "عنوان الملف الشخصي",
      "dashboard.viewMore": "عرض المزيد",
      "dashboard.completeNow": "أكمل الآن",
      "dashboard.majorGaps": "لا تزال هناك فجوات مؤسسية رئيسية. عالج العرض التقديمي والمقاييس والتحضير للعناية الواجبة للتحسين.",
      "dashboard.completeToSee": "أكمل تقييم نقاط IR لرؤية تصنيفك",
      "dashboard.foundationDreamer": "أنت تضع الطوب الأول لقصة مستثمرك. حافظ على الزخم مع سباقات السرعة الموجهة.",
      "dashboard.pricing": "الأسعار",
      "dashboard.uploadDocuments": "تحميل المستندات الخاصة بك",
      "dashboard.uploadDocumentsDesc": "تحميل المستندات المطلوبة لطلب الرهن العقاري الخاص بك",
      "dashboard.speakConsultant": "تحدث مع مستشار Conncct",
      "dashboard.speakConsultantDesc": "افتح الدعم المخصص، والأدوات المتقدمة، والوصول الكامل إلى المنصة - كلها مصممة لتسريع نمو عملك.",
      "dashboard.upgrade": "ترقية",
      "dashboard.readyToRaise": "هل أنت مستعد لجمع رأس المال؟",
      "dashboard.readyToRaiseDesc": "كل ما تحتاجه لتأمين المستثمرين - خطة عمل، تقييم، امتثال، وإعداد غرفة البيانات.",
      "dashboard.startNow": "ابدأ الآن",
      "dashboard.freeConsultation": "استشارة مجانية",
      "dashboard.freeConsultationDesc": "هل لديك سؤال؟ اسأل خبراءنا حول مسائل الأعمال، حسابك، منتجاتنا، والمزيد.",
      "dashboard.bookNow": "احجز الآن",
      "dashboard.taxFilings": "الضرائب والإيداعات للشركات",
      "dashboard.taxFilingsDesc": "تسجيل ضريبة القيمة المضافة والإقرارات، استشارة ضريبة الشركات والتقديم.",
      "dashboard.legalSupport": "دعم قانوني مصمم لمرحلتك",
      "dashboard.legalSupportDesc": "سواء كنت بدأت للتو أو تقوم بتوسيع عملك، يساعدك فريقنا القانوني على البقاء محمياً وجاهزاً للمستثمرين.",
      "dashboard.documents": "المستندات",
      "dashboard.consultations": "الاستشارات",
      "dashboard.scoreDetails": "تفاصيل النقاط",
      "dashboard.gapsBreakdown": "تفاصيل الفجوات",
      "dashboard.planBreakdown": "تفاصيل الخطة",
      "dashboard.viewLess": "عرض أقل",
      "dashboard.select": "تحديد",
      "dashboard.gaps.team": "الفريق والقيادة: أدوار رئيسية/مستشارون مفقودون (رئيسي).",
      "dashboard.gaps.market": "وضوح السوق والملاءمة: المشكلة/العميل لم يتم التحقق منه (رئيسي).",
      "dashboard.gaps.model": "نموذج العمل والتسعير: غير واضح/غير مجرب (رئيسي).",
      "dashboard.gaps.traction": "الزخم والإثبات: مستخدمون/إيرادات محدودة أو تجريبي (رئيسي).",
      "dashboard.gaps.financial": "الخطة المالية والمدى: لا يوجد نموذج / مدى قصير (رئيسي).",
      "dashboard.gaps.pitch": "العرض والمواد: القصة/العرض التقديمي ليس بدرجة المستثمر (متوسط).",
      "dashboard.gaps.compliance": "الامتثال والعناية الواجبة: النظافة القانونية/جدول رأس المال غير مكتمل (متوسط).",
      "dashboard.plan.team": "أضف مواهب أساسية + مستشارين (أغلق فجوات المهارات الحرجة).",
      "dashboard.plan.market": "تحقق من السوق (15-20 مقابلة، 3-5 خطابات نوايا/تجريبي).",
      "dashboard.plan.model": "حدد النموذج (التسعير، اقتصاديات الوحدة؛ اختبر مجموعة صغيرة).",
      "dashboard.plan.mvp": "ابنِ MVP والزخم الأول (مستخدمون/إيرادات؛ تتبع مؤشرات KPIs بسيطة).",
      "dashboard.plan.budget": "ميزانية 12-18 شهراً والمدى (تحكم في الحرق؛ حدد الهدف للجمع).",
      "dashboard.plan.pitch": "نسخة العرض 1 (10-12 شريحة) + أسئلة شائعة (قصة قائمة على الأدلة).",
      "dashboard.plan.dataroom": "بداية غرفة البيانات (تأسيس، جدول رأس المال، مقاييس، عقود).",
      "dashboard.foundation": "مؤسسة",
      "dashboard.capital": "رأس المال",
      "dashboard.mvp": "MVP",
      "dashboard.market/growth": "السوق/النمو",
      "dashboard.marketGrowth": "السوق/النمو",
      "dashboard.dreamer": "حالم",
      "dashboard.explorer": "مستكشف",
      "dashboard.builder": "باني",
      "dashboard.shaper": "مشكل",
      "dashboard.challenger": "متحدي",
      "dashboard.trailblazer": "رائد",
      "dashboard.visionary": "صاحب رؤية",
      "dashboard.welcome": "مرحبًا بعودتك،",
      "dashboard.subtitle": "إليك ما يحدث مع تطبيقاتك اليوم.",
      "dashboard.myApplications": "تطبيقاتي",
      "dashboard.createApp": "إنشاء تطبيق جديد",
      "dashboard.card.startups": "الشركات الناشئة",
      "dashboard.card.investors": "المستثمرون",
      "dashboard.card.partners": "الشركاء",
      "dashboard.viewDashboard": "عرض لوحة المعلومات",
      "dashboard.recentActivity": "النشاط الأخير",
      "dashboard.noActivity": "لا يوجد نشاط حديث",

      "dashboard.risk.critical": "خطر حرج",
      "dashboard.risk.veryHigh": "خطر مرتفع جداً",
      "dashboard.risk.high": "خطر مرتفع",
      "dashboard.risk.medium": "خطر متوسط",
      "dashboard.risk.semiReady": "شبه جاهز",
      "dashboard.risk.investorReady": "جاهز للمستثمر",
      "dashboard.risk.exceptional": "استثنائي",

      "dashboard.loading": "جاري تحميل لوحة المعلومات...",
      "dashboard.userFallback": "مستخدم",
      "dashboard.notAssessed": "لم يتم التقييم",
      "sidebar.menu.dashboard": "لوحة القيادة",
      "sidebar.menu.raisingRequests": "طلبات التمويل",
      "sidebar.menu.partners": "الشركاء",
      "sidebar.menu.admins": "المسؤولون",
      "sidebar.menu.settings": "الإعدادات",
      "sidebar.menu.inviteAdmin": "دعوة مسؤول",
      "sidebar.menu.adminProfile": "ملفي الشخصي",

      // Admin Raising Requests
      "admin.raising.clientOverview": "نظرة عامة على العملاء",
      "admin.raising.newClients": "عملاء جدد",
      "admin.raising.last30Days": "آخر 30 يومًا",
      "admin.raising.allClients": "جميع العملاء",
      "admin.raising.totalRequests": "إجمالي الطلبات",
      "admin.raising.searchPlaceholder": "البحث عن طريق رقم الحساب، البريد الإلكتروني، الهاتف أو الاسم",
      "admin.raising.loading": "تحميل طلبات التمويل...",
      "admin.raising.fastFilters": "مرشحات سريعة",
      "admin.raising.newRequests": "طلبات جديدة",
      "admin.raising.applyFilters": "تطبيق المرشحات",
      "admin.raising.referralTable": "جدول الإحالات",
      "admin.raising.showingRange": "عرض {{count}} من إجمالي {{total}} طلب تمويل",
      "admin.raising.refresh": "تحديث",
      "admin.raising.noRequests": "لا توجد طلبات تمويل تطابق المرشحات الحالية.",
      "admin.raising.unnamedFounder": "مؤسس لم يتم تسميته",
      "admin.raising.productLabelFallback": "تمويل",
      "admin.raising.placeholder.startDate": "تاريخ البدء",
      "admin.raising.placeholder.endDate": "تاريخ الانتهاء",
      "admin.raising.placeholder.allCountries": "جميع البلدان",
      "admin.raising.view": "عرض",

      // Admin Profile
      "admin.profile.title": "معلوماتك",
      "admin.profile.fullName": "الاسم الكامل",
      "admin.profile.email": "البريد الإلكتروني",
      "admin.profile.accountStatus": "حالة الحساب:",
      "admin.profile.deleteAccount": "حذف الحساب",
      "admin.profile.deleteConfirm": "هل أنت متأكد أنك تريد حذف حسابك؟ لا يمكن التراجع عن هذا الإجراء.",

      // Admin Settings
      "admin.settings.title": "إعدادات المسؤول",
      "admin.settings.changePassword": "تغيير كلمة المرور",
      "admin.settings.updatePasswordDesc": "تحديث كلمة مرور حساب المسؤول الخاص بك.",
      "admin.settings.currentPassword": "كلمة المرور الحالية",
      "admin.settings.newPassword": "كلمة المرور الجديدة",
      "admin.settings.currentPasswordPlaceholder": "أدخل كلمة المرور الحالية",
      "admin.settings.newPasswordPlaceholder": "أدخل كلمة المرور الجديدة",
      "admin.settings.cancel": "إلغاء",
      "admin.settings.update": "تحديث كلمة المرور",
      "admin.settings.updating": "جاري التحديث...",
      "admin.settings.error.fillAll": "يرجى تعبئة جميع حقول كلمة المرور",
      "admin.settings.error.minChars": "يجب أن تتكون كلمة المرور الجديدة من 6 أحرف على الأقل",
      "admin.settings.success": "تم تغيير كلمة المرور بنجاح! جاري تسجيل الخروج...",
      "admin.settings.error.failed": "فشل تغيير كلمة المرور",
      "admin.settings.error.generic": "خطأ في تغيير كلمة المرور. يرجى المحاولة مرة أخرى.",

      // Admin Invitation
      "admin.invitation.sendTitle": "إرسال دعوة مسؤول",
      "admin.invitation.nameLabel": "اسم المسؤول",
      "admin.invitation.namePlaceholder": "أدخل اسم المسؤول",
      "admin.invitation.emailLabel": "البريد الإلكتروني للمسؤول",
      "admin.invitation.emailPlaceholder": "أدخل البريد الإلكتروني",
      "admin.invitation.note": "ملاحظة: سيتم إنشاء كلمة مرور آمنة تلقائيًا وإرسالها إلى البريد الإلكتروني للمسؤول. يمكن للمسؤول تسجيل الدخول فورًا باستخدام بيانات الاعتماد المقدمة.",
      "admin.invitation.sendButton": "إرسال الدعوة",
      "admin.invitation.restrictedTitle": "وصول مقيد",
      "admin.invitation.restrictedDesc": "يمكن للمسؤول المتميز فقط إرسال الدعوات.",
      "admin.invitation.adminsTitle": "المسؤولون",
      "admin.invitation.adminsCount": "مسؤول",
      "admin.invitation.tableTitle": "جدول الدعوات",
      "admin.invitation.header.name": "الاسم",
      "admin.invitation.header.email": "البريد الإلكتروني",
      "admin.invitation.header.invitedOn": "تاريخ الدعوة",
      "admin.invitation.header.status": "الحالة",
      "admin.invitation.header.action": "الإجراء",
      "admin.invitation.loading": "جاري التحميل...",
      "admin.invitation.noAdmins": "لم يتم العثور على مسؤولين",
      "admin.invitation.status.invited": "مدعو",
      "admin.invitation.status.active": "نشط",
      "admin.invitation.delete": "حذف",
      "admin.invitation.alert.enterName": "يرجى إدخال اسم المسؤول",
      "admin.invitation.alert.enterEmail": "يرجى إدخال عنوان بريد إلكتروني صالح",
      "admin.invitation.alert.sentSuccess": "تم إرسال دعوة المسؤول بنجاح!",
      "admin.invitation.alert.sentEmail": "تم إرسال بريد إلكتروني إلى {{email}} مع بيانات اعتماد تسجيل الدخول.",
      "admin.invitation.alert.deleteConfirm": "هل أنت متأكد أنك تريد إزالة المسؤول \"{{name}}\"؟ لا يمكن التراجع عن هذا الإجراء.",
      "admin.invitation.alert.deleteSuccess": "تمت إزالة المسؤول بنجاح",
      "admin.invitation.alert.deleteFail": "فشل إزالة المسؤول",
      // User Settings Page
      "settings.breadcrumb.myApplications": "تطبيقاتي",
      "settings.breadcrumb.settings": "الإعدادات",
      "settings.title": "الإعدادات",
      "settings.password.title": "كلمة المرور",
      "settings.password.subtitle": "إدارة كلمة مرور حسابك وتفضيلات الفوترة.",
      "settings.password.oldLabel": "كلمة المرور القديمة",
      "settings.password.oldPlaceholder": "أدخل كلمة المرور",
      "settings.password.newLabel": "كلمة المرور الجديدة",
      "settings.password.newPlaceholder": "أدخل كلمة المرور الجديدة",
      "settings.password.cancel": "إلغاء",
      "settings.password.continue": "متابعة",
      "settings.password.continuing": "جارٍ المتابعة...",
      "settings.password.error.fillAll": "يرجى ملء جميع حقول كلمة المرور",
      "settings.password.error.minChars": "يجب أن تتكون كلمة المرور الجديدة من 6 أحرف على الأقل",
      "settings.password.success": "تم تغيير كلمة المرور بنجاح! جارٍ تسجيل الخروج...",
      "settings.password.error.failed": "فشل تغيير كلمة المرور",
      "settings.password.error.generic": "خطأ في تغيير كلمة المرور. يرجى المحاولة مرة أخرى.",
      "settings.deleteAccount.title": "حذف الحساب",
      "settings.deleteAccount.subtitle": "حذف حسابك وجميع محتوياتك بشكل دائم. هذا الإجراء لا يمكن التراجع عنه.",
      "settings.deleteAccount.button": "حذف الحساب",
      "settings.payment.comingSoon": "تكامل الدفع قريباً!",

      // Demo Schedule (Calendly) Page
      "demoSchedule.title": "هل أنت مستعد للتحدث؟ اختر وقتاً أدناه.",
      "demoSchedule.subtitle": "احجز مكالمة مدتها 15 دقيقة مع فريقنا لمناقشة كيف يمكن لـ Conncct مساعدة عملك.",
      "demoSchedule.noTime": "لا تجد وقتاً مناسباً؟",
      "demoSchedule.contactUs": "تواصل معنا مباشرة",
      "demoSchedule.footer": "بجدولة مكالمة، فإنك توافق على",
      "demoSchedule.termsOfService": "شروط الخدمة",
      "demoSchedule.and": "و",
      "demoSchedule.privacyPolicy": "سياسة الخصوصية",


    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    // Don't set lng here - let LanguageDetector handle it from localStorage
    interpolation: {
      escapeValue: false
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    }
  });

i18n.on('languageChanged', (lng) => {
  document.dir = lng === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.lang = lng;
  // Persist language selection to localStorage
  localStorage.setItem('i18nextLng', lng);
});

export default i18n;
